import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { toast } from 'sonner';
import { CurrencyDisplay, DirhamIcon } from '../components/DirhamSymbol';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import Pagination from '../components/Pagination';
import { 
  Users, UserPlus, Receipt, RefreshCw, Loader2, 
  Plus, Eye, Trash2, CheckCircle2, Clock, Upload, FileText,
  ArrowRight, Building2, Phone, MapPin, CreditCard, Banknote, Download, Image, File, Edit, PauseCircle, Search, ChevronsUpDown, Check, GraduationCap, Wallet, MoreVertical, AlertCircle, ExternalLink, FileUp, FileSpreadsheet
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { cn } from '../lib/utils';
import { formatDateDisplay, truncateName, truncateCompanyName } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import CreateDocuments from '../components/CreateDocuments';
import { formatErrorMessage } from '../utils/formatError';

const API = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api';

// Debug log for API URL (helps diagnose production issues)
if (process.env.NODE_ENV === 'development' || !process.env.REACT_APP_BACKEND_URL) {
  console.log('TAP API URL:', API);
}

const TAP = ({ user }) => {
  const { isMobile, isTablet } = useDeviceDetection();
  // State
  const [activeTab, setActiveTab] = useState('enroll');
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [lessees, setLessees] = useState([]);
  const [config, setConfig] = useState({});
  const [drivingSchools, setDrivingSchools] = useState([]);
  const [searchTerm, setSearchTerm] = useState(''); // Search filter for candidates
  const [filters, setFilters] = useState({ status: 'all' }); // Status filter for dashboard cards
  // Pagination — the search bar filters across ALL candidates and then
  // paginates the results client-side so users never miss records that
  // fall off page 1. Enroll (Candidates) and Expenses tabs each keep
  // their own page cursor so switching tabs doesn't reset navigation.
  const CANDIDATES_PER_PAGE = 50;
  const [enrollPage, setEnrollPage] = useState(1);
  const [expensesPage, setExpensesPage] = useState(1);
  
  // Ready-stage orphan scanner state
  const [readyOrphanDialogOpen, setReadyOrphanDialogOpen] = useState(false);
  const [readyOrphanScan, setReadyOrphanScan] = useState(null);
  const [readyOrphanScanRunning, setReadyOrphanScanRunning] = useState(false);
  const [readyOrphanImporting, setReadyOrphanImporting] = useState(false);
  const [readyOrphanImportResult, setReadyOrphanImportResult] = useState(null);

  // Bulk update Driving License Info via Excel
  const [bulkDLExcelDialogOpen, setBulkDLExcelDialogOpen] = useState(false);
  const [bulkDLExcelFile, setBulkDLExcelFile] = useState(null);
  const [bulkDLExcelRunning, setBulkDLExcelRunning] = useState(false);
  const [bulkDLExcelResults, setBulkDLExcelResults] = useState(null);

  // Dialog States
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [bulkUploadDocType, setBulkUploadDocType] = useState('loan_agreement');
  const [bulkUploadFiles, setBulkUploadFiles] = useState([]);
  const [bulkUploadResults, setBulkUploadResults] = useState(null);
  const [bulkUploadRunning, setBulkUploadRunning] = useState(false);
  // Progress tracking so users see the batch counter roll on large
  // uploads (batches of 10 files to stay under the proxy read-timeout).
  const [bulkUploadProgress, setBulkUploadProgress] = useState({ done: 0, total: 0 });
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  // Non-null when the dialog is opened to EDIT an existing expense.
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseListDialogOpen, setExpenseListDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [candidateDetailsDialogOpen, setCandidateDetailsDialogOpen] = useState(false);
  const [editCandidateDialogOpen, setEditCandidateDialogOpen] = useState(false);
  const [addDrivingSchoolDialogOpen, setAddDrivingSchoolDialogOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [drivingSchoolPopoverOpen, setDrivingSchoolPopoverOpen] = useState(false);
  
  // New Driving School Form
  const [newSchoolForm, setNewSchoolForm] = useState({ name: '', city: '', address: '' });
  
  // Passport validation state
  const [passportValidation, setPassportValidation] = useState({ checking: false, error: null, valid: true });
  const [editPassportValidation, setEditPassportValidation] = useState({ checking: false, error: null, valid: true });
  
  // Form States
  const [enrollForm, setEnrollForm] = useState({
    name: '', nationality: '', date_of_birth: '', home_address: '', email: '',
    uae_mobile: '', home_country_mobile: '',
    ref1_name: '', ref1_mobile: '', ref1_address: '',
    ref2_name: '', ref2_mobile: '', ref2_address: '',
    license_number: '', license_issue_date: '', license_expiry_date: '',
    home_country_id_card: '', passport_number: '',
    lessee_id: '', lessee_name: '', advance_payment: '', loan_contract_value: '', no_of_installments: '',
    // Document files
    passport_copy: null, passport_photo: null, home_license: null, home_id_card: null,
    employment_agreement: null, loan_agreement: null, receipt_cash_paid: null,
    // Additional optional documents
    mb_mohre: null, st_mohre: null, entry_permit: null, medical_report: null,
    emirates_id_doc: null, visa_doc: null
  });
  
  const [expenseForm, setExpenseForm] = useState({
    expense_type: '', expense_subtype: '', description: '',
    driving_school_name: '', driving_school_joining_date: '',
    driving_payment_type: '', // "pay_as_you_go" or "fixed_lumpsum"
    accommodation_location: '', accommodation_month_year: '',
    amount: '', expense_date: '', paid_to: '',
    payment_method: '', payment_source: '',
    invoice: null, receipt: null,
    // Multi-file support
    new_invoices: [], new_receipts: [],
    existing_invoice_files: [], existing_receipt_files: [],
  });

  // Advance Payment States
  const [advancePayments, setAdvancePayments] = useState([]);
  const [advancePaymentForm, setAdvancePaymentForm] = useState({
    amount: '',
    payment_date: '',
    notes: '',
    proof_file: null
  });
  const [addingAdvance, setAddingAdvance] = useState(false);
  const [selectedAdvancePayment, setSelectedAdvancePayment] = useState(null);
  const [advanceDetailDialogOpen, setAdvanceDetailDialogOpen] = useState(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('TAP: No auth token found in localStorage');
    }
    return { Authorization: `Bearer ${token || ''}` };
  };

  // Fetch full candidate data (including documents) for viewing
  const fetchFullCandidate = async (candidateId) => {
    try {
      const response = await axios.get(`${API}/tap/candidates/${candidateId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching candidate details:', error);
      toast.error('Could not load full candidate data');
      return null;
    }
  };

  // Helper to compute age from date_of_birth
  const computeAge = (dob) => {
    if (!dob) return null;
    try {
      let date;
      if (dob.includes('/')) {
        const parts = dob.split('/');
        date = new Date(parts[2], parts[1] - 1, parts[0]);
      } else if (dob.includes('-')) {
        const parts = dob.split('-');
        if (parts[0].length === 4) {
          date = new Date(dob);
        } else {
          date = new Date(parts[2], parts[1] - 1, parts[0]);
        }
      } else {
        date = new Date(dob);
      }
      if (isNaN(date.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - date.getFullYear();
      const m = today.getMonth() - date.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < date.getDate())) age--;
      return age > 0 ? age : null;
    } catch { return null; }
  };

  // Helper function to download file - handles both base64 data URLs and OneDrive URLs
  const downloadFile = (url, filename) => {
    try {
      if (!url) {
        toast.error('No file available');
        return;
      }
      
      // If it's an OneDrive/HTTP URL, open in new tab
      if (url.startsWith('http://') || url.startsWith('https://')) {
        window.open(url, '_blank');
        return;
      }
      
      // If it's a base64 data URL, download directly
      if (url.startsWith('data:')) {
        const [header, base64Data] = url.split(',');
        if (!base64Data) {
          toast.error('Invalid file data');
          return;
        }

        const mimeMatch = header.match(/data:([^;]+)/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';

        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        
        const blob = new Blob([byteArray], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
        toast.success('File downloaded successfully');
        return;
      }
      
      toast.error('Invalid file format');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  // Check if a URL is a valid document URL (either base64 or OneDrive)
  const hasDocumentUrl = (url) => {
    if (!url) return false;
    return url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://');
  };

  // Get file extension from data URL or OneDrive URL
  const getFileExtension = (url) => {
    if (!url) return '.pdf';
    // Handle base64 data URLs
    if (url.startsWith('data:')) {
      if (url.includes('image/jpeg') || url.includes('image/jpg')) return '.jpg';
      if (url.includes('image/png')) return '.png';
      if (url.includes('application/pdf')) return '.pdf';
    }
    // Handle HTTP URLs
    if (url.startsWith('http')) {
      const match = url.match(/\.(jpg|jpeg|png|pdf|gif|webp)(\?|$)/i);
      if (match) return `.${match[1].toLowerCase()}`;
    }
    return '.pdf';
  };

  // Passport check timeout ref
  const passportCheckTimeout = useRef(null);

  // Check passport duplicate function
  const checkPassportDuplicate = useCallback(async (passportNumber, excludeId = null, isEdit = false) => {
    if (!passportNumber || passportNumber.trim().length < 3) {
      if (isEdit) {
        setEditPassportValidation({ checking: false, error: null, valid: true });
      } else {
        setPassportValidation({ checking: false, error: null, valid: true });
      }
      return;
    }
    
    const setValidation = isEdit ? setEditPassportValidation : setPassportValidation;
    setValidation({ checking: true, error: null, valid: true });
    
    try {
      const url = excludeId 
        ? `${API}/tap/check-passport/${encodeURIComponent(passportNumber)}?exclude_id=${excludeId}`
        : `${API}/tap/check-passport/${encodeURIComponent(passportNumber)}`;
      
      const response = await axios.get(url, { headers: getAuthHeader() });
      
      if (response.data.exists) {
        setValidation({ 
          checking: false, 
          error: response.data.message,
          valid: false,
          existingName: response.data.name
        });
      } else {
        setValidation({ checking: false, error: null, valid: true });
      }
    } catch (error) {
      console.error('Error checking passport:', error);
      setValidation({ checking: false, error: null, valid: true });
    }
  }, []);

  // Debounced passport check with timeout
  const debouncedPassportCheck = useCallback((value, excludeId = null, isEdit = false) => {
    if (passportCheckTimeout.current) {
      clearTimeout(passportCheckTimeout.current);
    }
    passportCheckTimeout.current = setTimeout(() => {
      checkPassportDuplicate(value, excludeId, isEdit);
    }, 500);
  }, [checkPassportDuplicate]);

  // Fetch Functions
  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      // Request every candidate in a single hit — the list endpoint
      // defaults to only 200. We handle pagination client-side (below)
      // so the search bar can match across the entire dataset.
      console.log('TAP: Fetching candidates from:', `${API}/tap/candidates?limit=5000`);
      const response = await axios.get(`${API}/tap/candidates?limit=5000`, { headers: getAuthHeader() });
      setCandidates(response.data.candidates || []);
      console.log('TAP: Successfully fetched', (response.data.candidates || []).length, 'candidates');
    } catch (error) {
      console.error('TAP: Error fetching candidates:', error);
      console.error('TAP: Error response:', error.response?.status, error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Failed to fetch TAP candidates: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  // ---------- Filtered / paginated candidate lists ------------------------
  // Search-and-filter runs across the FULL candidates array so records on
  // page 2+ are still discoverable. We then slice for the visible page.
  const filteredEnrollCandidates = useMemo(() => {
    const search = (searchTerm || '').toLowerCase();
    return candidates
      .filter(candidate => {
        if (filters.status !== 'all' && candidate.status !== filters.status) return false;
        if (!search) return true;
        return (
          (candidate.name || '').toLowerCase().includes(search) ||
          (candidate.employee_unique_id || '').toLowerCase().includes(search) ||
          (candidate.uae_mobile || '').toLowerCase().includes(search) ||
          (candidate.home_country_mobile || '').toLowerCase().includes(search) ||
          (candidate.passport_number || candidate.passport_no || '').toLowerCase().includes(search) ||
          (candidate.nationality || '').toLowerCase().includes(search) ||
          (candidate.lessee_name || '').toLowerCase().includes(search) ||
          (candidate.email || '').toLowerCase().includes(search)
        );
      })
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [candidates, searchTerm, filters.status]);

  const filteredExpenseCandidates = useMemo(() => {
    const search = (searchTerm || '').toLowerCase();
    return candidates
      .filter(candidate => {
        if (!search) return true;
        return (
          (candidate.name || '').toLowerCase().includes(search) ||
          (candidate.employee_unique_id || '').toLowerCase().includes(search) ||
          (candidate.lessee_name || '').toLowerCase().includes(search)
        );
      })
      .slice()
      .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [candidates, searchTerm]);

  // Reset to page 1 whenever the filter set changes, otherwise a narrow
  // search could leave the user stranded on an empty higher page.
  useEffect(() => { setEnrollPage(1); }, [searchTerm, filters.status]);
  useEffect(() => { setExpensesPage(1); }, [searchTerm]);

  const paginatedEnrollCandidates = useMemo(() => {
    const start = (enrollPage - 1) * CANDIDATES_PER_PAGE;
    return filteredEnrollCandidates.slice(start, start + CANDIDATES_PER_PAGE);
  }, [filteredEnrollCandidates, enrollPage]);

  const paginatedExpenseCandidates = useMemo(() => {
    const start = (expensesPage - 1) * CANDIDATES_PER_PAGE;
    return filteredExpenseCandidates.slice(start, start + CANDIDATES_PER_PAGE);
  }, [filteredExpenseCandidates, expensesPage]);

  // ---------- Expenses Export (Excel + PDF) ------------------------------
  // Uses the /tap/expenses-report endpoint which returns one row per
  // candidate with expenses pivoted by expense_type. Falls back to a client
  // toast if the endpoint is unavailable.
  const fetchExpensesReport = async () => {
    const res = await axios.get(`${API}/tap/expenses-report`, { headers: getAuthHeader() });
    return res.data || { expense_types: [], rows: [] };
  };

  const formatExpenseHeader = (raw) =>
    String(raw).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const buildExportRows = (report) => {
    const types = report.expense_types || [];
    const rows = (report.rows || []).map(r => ({
      'UID': r.uid || '',
      'Rider Name': r.name || '',
      'Emirates ID': r.emirates_id || '',
      'Nationality': r.nationality || '',
      'Loan Contract Value': Number(r.loan_contract_value || 0),
      ...Object.fromEntries(types.map(t => [formatExpenseHeader(t), Number(r[t] || 0)])),
      'Total Expenses': Number(r.total_expenses || 0),
    }));
    // Totals row at bottom
    const totals = { 'UID': '', 'Rider Name': 'TOTAL', 'Emirates ID': '', 'Nationality': '',
      'Loan Contract Value': rows.reduce((s, x) => s + (x['Loan Contract Value'] || 0), 0) };
    for (const t of types) {
      const key = formatExpenseHeader(t);
      totals[key] = rows.reduce((s, x) => s + (x[key] || 0), 0);
    }
    totals['Total Expenses'] = rows.reduce((s, x) => s + (x['Total Expenses'] || 0), 0);
    return { types, rows, totals };
  };

  const handleExpensesExportExcel = async () => {
    try {
      const report = await fetchExpensesReport();
      const { rows, totals } = buildExportRows(report);
      if (!rows.length) { toast.error('Nothing to export'); return; }
      const ws = XLSX.utils.json_to_sheet([...rows, totals]);
      // Bold the totals row (simple approach — set font on last row cells)
      const lastRowIdx = rows.length + 1; // header = row 1
      const ref = ws['!ref'];
      if (ref) {
        const range = XLSX.utils.decode_range(ref);
        for (let c = range.s.c; c <= range.e.c; c++) {
          const cellRef = XLSX.utils.encode_cell({ r: lastRowIdx, c });
          if (ws[cellRef]) ws[cellRef].s = { font: { bold: true } };
        }
      }
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'TAP Expenses');
      XLSX.writeFile(wb, `tap_expenses_${new Date().toISOString().slice(0, 10)}.xlsx`);
      toast.success(`Exported ${rows.length} rows to Excel`);
    } catch (e) {
      toast.error('Excel export failed — see console');
      console.error(e);
    }
  };

  const handleExpensesExportPDF = async () => {
    try {
      const report = await fetchExpensesReport();
      const { rows, totals } = buildExportRows(report);
      if (!rows.length) { toast.error('Nothing to export'); return; }
      const { default: jsPDF } = await import('jspdf');
      const autoTableMod = await import('jspdf-autotable');
      const autoTable = autoTableMod.default || autoTableMod.autoTable;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a3' });
      doc.setFontSize(14);
      doc.text('TAP — Expense Tracking Report', 40, 40);
      doc.setFontSize(9);
      doc.text(`Generated ${new Date().toLocaleString()} • ${rows.length} candidates`, 40, 56);
      const headers = Object.keys(rows[0]);
      autoTable(doc, {
        startY: 72,
        head: [headers],
        body: rows.map(r => headers.map(h => (typeof r[h] === 'number' ? r[h].toLocaleString() : r[h]))),
        foot: [headers.map(h => (typeof totals[h] === 'number' ? totals[h].toLocaleString() : totals[h] || ''))],
        styles: { fontSize: 7, cellPadding: 3, overflow: 'linebreak' },
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
        footStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 250, 255] },
        margin: { left: 20, right: 20 },
      });
      doc.save(`tap_expenses_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success(`Exported ${rows.length} rows to PDF`);
    } catch (e) {
      toast.error('PDF export failed — see console');
      console.error(e);
    }
  };

  const fetchLessees = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lessees`, { headers: getAuthHeader() });
      setLessees(response.data.lessees || response.data || []);
    } catch (error) {
      console.error('Failed to fetch lessees');
    }
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tap/config`, { headers: getAuthHeader() });
      setConfig(response.data);
    } catch (error) {
      console.error('Failed to fetch config');
    }
  }, []);

  const fetchDrivingSchools = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tap/driving-schools`, { headers: getAuthHeader() });
      setDrivingSchools(response.data.schools || []);
    } catch (error) {
      console.error('Failed to fetch driving schools');
    }
  }, []);

  // Fetch advance payments for a candidate
  const fetchAdvancePayments = useCallback(async (candidateId) => {
    try {
      const response = await axios.get(`${API}/tap/advance-payments/${candidateId}`, { headers: getAuthHeader() });
      setAdvancePayments(response.data.payments || []);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch advance payments:', error);
      setAdvancePayments([]);
      return { payments: [], total_advance: 0 };
    }
  }, []);

  // Add new advance payment
  const handleAddAdvancePayment = async () => {
    if (!selectedCandidate?.id) return;
    if (!advancePaymentForm.amount || advancePaymentForm.amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!advancePaymentForm.payment_date) {
      toast.error('Please enter payment date');
      return;
    }

    setAddingAdvance(true);
    try {
      const formData = new FormData();
      formData.append('candidate_id', selectedCandidate.id);
      formData.append('amount', advancePaymentForm.amount);
      formData.append('payment_date', advancePaymentForm.payment_date);
      formData.append('notes', advancePaymentForm.notes || '');
      
      if (advancePaymentForm.proof_file) {
        formData.append('proof_file', advancePaymentForm.proof_file);
      }

      const response = await axios.post(`${API}/tap/advance-payments`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Advance payment added successfully');
      
      // Reset form
      setAdvancePaymentForm({ amount: '', payment_date: '', notes: '', proof_file: null });
      
      // Refresh advance payments list
      await fetchAdvancePayments(selectedCandidate.id);
      
      // Update candidate in list
      fetchCandidates();
    } catch (error) {
      console.error('Failed to add advance payment:', error);
      toast.error(formatErrorMessage(error, 'Failed to add advance payment'));
    } finally {
      setAddingAdvance(false);
    }
  };

  // Delete advance payment
  const handleDeleteAdvancePayment = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this advance payment?')) return;
    
    try {
      await axios.delete(`${API}/tap/advance-payments/${paymentId}`, { headers: getAuthHeader() });
      toast.success('Advance payment deleted');
      
      // Refresh advance payments
      if (selectedCandidate?.id) {
        await fetchAdvancePayments(selectedCandidate.id);
      }
      
      // Refresh candidates list
      fetchCandidates();
      
      // Close detail dialog if open
      setAdvanceDetailDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete advance payment:', error);
      toast.error('Failed to delete advance payment');
    }
  };

  useEffect(() => {
    fetchCandidates();
    fetchLessees();
    fetchConfig();
    fetchDrivingSchools();
  }, [fetchCandidates, fetchLessees, fetchConfig, fetchDrivingSchools]);

  // Handler for adding a new driving school
  const handleAddDrivingSchool = async () => {
    if (!newSchoolForm.name) {
      toast.error('School name is required');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', newSchoolForm.name);
      formData.append('city', newSchoolForm.city);
      formData.append('address', newSchoolForm.address);
      
      const response = await axios.post(`${API}/tap/driving-schools`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Driving school added successfully');
      setAddDrivingSchoolDialogOpen(false);
      setNewSchoolForm({ name: '', city: '', address: '' });
      fetchDrivingSchools();
      
      // Auto-select the newly added school
      setExpenseForm({...expenseForm, driving_school_name: response.data.school.name});
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to add driving school'));
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleEnrollSubmit = async (forceAllowDuplicateName = false) => {
    if (!enrollForm.name) {
      toast.error('Name is required');
      return;
    }
    if (!enrollForm.passport_copy) {
      toast.error('Passport Copy is mandatory');
      return;
    }
    if (!enrollForm.passport_photo) {
      toast.error('Passport Size Photo is mandatory');
      return;
    }
    
    // Check passport validation before submit
    if (!passportValidation.valid) {
      toast.error('Please fix the passport number issue before enrolling');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      // All document file fields including new optional ones
      const fileFields = ['passport_copy', 'passport_photo', 'home_license', 'home_id_card', 
                          'employment_agreement', 'loan_agreement', 'receipt_cash_paid',
                          'mb_mohre', 'st_mohre', 'entry_permit', 'medical_report',
                          'emirates_id_doc', 'visa_doc'];
      
      Object.keys(enrollForm).forEach(key => {
        // Handle file uploads separately
        if (fileFields.includes(key)) {
          if (enrollForm[key]) {
            formData.append(key, enrollForm[key]);
          }
        } else {
          formData.append(key, enrollForm[key] || '');
        }
      });
      if (forceAllowDuplicateName) {
        formData.append('force_allow_duplicate_name', 'true');
      }
      
      await axios.post(`${API}/tap/candidates`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Candidate enrolled successfully');
      setEnrollDialogOpen(false);
      setEnrollForm({
        name: '', nationality: '', date_of_birth: '', home_address: '', email: '',
        uae_mobile: '', home_country_mobile: '',
        ref1_name: '', ref1_mobile: '', ref1_address: '',
        ref2_name: '', ref2_mobile: '', ref2_address: '',
        license_number: '', license_issue_date: '', license_expiry_date: '',
        home_country_id_card: '', passport_number: '',
        lessee_id: '', lessee_name: '', advance_payment: '', loan_contract_value: '', no_of_installments: '',
        passport_copy: null, passport_photo: null, home_license: null, home_id_card: null,
        employment_agreement: null, loan_agreement: null, receipt_cash_paid: null,
        mb_mohre: null, st_mohre: null, entry_permit: null, medical_report: null,
        emirates_id_doc: null, visa_doc: null
      });
      fetchCandidates();
    } catch (error) {
      // Backend returns 409 with { code: 'duplicate_name', message, same_passport, existing_id }
      const detail = error?.response?.data?.detail;
      const isDupName = error?.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'duplicate_name';
      if (isDupName && !forceAllowDuplicateName) {
        const samePassport = !!detail.same_passport;
        const confirmMsg = samePassport
          ? `A candidate named "${detail.existing_name}" with the SAME passport number already exists in TAP. This is almost certainly a duplicate.\n\nDo you still want to add this record?`
          : `A candidate named "${detail.existing_name}" already exists in TAP (different or missing passport).\n\nAre you sure this is a different person? Click OK to add anyway.`;
        if (window.confirm(confirmMsg)) {
          setLoading(false);
          return handleEnrollSubmit(true);
        }
        toast.info('Enrollment cancelled — no duplicate created.');
      } else {
        toast.error(formatErrorMessage(error, 'Failed to enroll candidate'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async () => {
    if (!selectedCandidate || !expenseForm.expense_type || !expenseForm.amount) {
      toast.error('Please fill required fields');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      // Only append candidate_id on CREATE — the update endpoint doesn't
      // expect it (candidate_id is immutable once the expense is booked).
      if (!editingExpenseId) {
        formData.append('candidate_id', selectedCandidate.id);
      }
      Object.keys(expenseForm).forEach(key => {
        if (key === 'invoice' || key === 'receipt') {
          // Legacy single-file field (keep for backwards compat; usually null now)
          if (expenseForm[key] instanceof window.File) formData.append(key, expenseForm[key]);
        } else if (key === 'new_invoices' || key === 'new_receipts') {
          // Multi-file: append each File individually under the plural field name
          const fieldName = key === 'new_invoices' ? 'invoices' : 'receipts';
          (expenseForm[key] || []).forEach(f => {
            if (f instanceof window.File) formData.append(fieldName, f);
          });
        } else if (key === 'existing_invoice_files' || key === 'existing_receipt_files') {
          // Send JSON list of docs the user wants to KEEP (only on edit)
          if (editingExpenseId) {
            const keepField = key === 'existing_invoice_files' ? 'keep_invoice_files' : 'keep_receipt_files';
            formData.append(keepField, JSON.stringify(expenseForm[key] || []));
          }
        } else if (key === 'existing_invoice_url' || key === 'existing_receipt_url') {
          // meta-only fields; not sent
        } else {
          formData.append(key, expenseForm[key] || '');
        }
      });
      
      if (editingExpenseId) {
        await axios.put(`${API}/tap/expenses/${editingExpenseId}`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/tap/expenses`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Expense added successfully');
      }
      setExpenseDialogOpen(false);
      setEditingExpenseId(null);
      setExpenseForm({
        expense_type: '', expense_subtype: '', description: '',
        driving_school_name: '', driving_school_joining_date: '',
        driving_payment_type: '',
        accommodation_location: '', accommodation_month_year: '',
        amount: '', expense_date: '', paid_to: '',
        payment_method: '', payment_source: '',
        invoice: null, receipt: null,
        new_invoices: [], new_receipts: [],
        existing_invoice_files: [], existing_receipt_files: [],
      });
      fetchCandidates();
    } catch (error) {
      toast.error(formatErrorMessage(error, editingExpenseId ? 'Failed to update expense' : 'Failed to add expense'));
    } finally {
      setLoading(false);
    }
  };

  // Open the Expense dialog pre-populated with an existing row so the
  // operator can amend any field or replace the uploaded invoice/receipt.
  // The row we receive from the Account Statement list is SLIM (file
  // fields stripped for speed) — hydrate it against the single-expense
  // endpoint so the edit form shows current invoice/receipt chips.
  const handleEditExpense = async (expense) => {
    setEditingExpenseId(expense.id);
    let full = expense;
    // Only hit the hydration endpoint when the row is missing files but
    // the presence flags say there ARE files. Rows already loaded with
    // `include_files=true` (legacy) will short-circuit.
    const needsHydration = (expense.has_invoice_url || expense.has_receipt_url
        || (expense.invoice_files_count || 0) > 0
        || (expense.receipt_files_count || 0) > 0)
        && !expense.invoice_url && !expense.receipt_url
        && !(expense.invoice_files || []).length
        && !(expense.receipt_files || []).length;
    if (needsHydration) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/tap/expenses/single/${expense.id}`,
          { headers: getAuthHeader() }
        );
        full = res.data || expense;
      } catch (_) {
        // Fall back to whatever we have — user can still edit
        // metadata; only the doc chips will be missing.
      }
    }
    // Normalise existing docs to [{url, filename}] shape, falling back to
    // the legacy single-URL field when the multi-file array isn't present.
    const invArr = Array.isArray(full.invoice_files) && full.invoice_files.length
      ? full.invoice_files.map(f => ({ url: f.url || '', filename: f.filename || '' }))
      : (full.invoice_url ? [{ url: full.invoice_url, filename: '' }] : []);
    const recArr = Array.isArray(full.receipt_files) && full.receipt_files.length
      ? full.receipt_files.map(f => ({ url: f.url || '', filename: f.filename || '' }))
      : (full.receipt_url ? [{ url: full.receipt_url, filename: '' }] : []);
    setExpenseForm({
      expense_type: full.expense_type || '',
      expense_subtype: full.expense_subtype || '',
      description: full.description || '',
      driving_school_name: full.driving_school_name || '',
      driving_school_joining_date: full.driving_school_joining_date || '',
      driving_payment_type: full.driving_payment_type || '',
      accommodation_location: full.accommodation_location || '',
      accommodation_month_year: full.accommodation_month_year || '',
      amount: full.amount || '',
      expense_date: full.expense_date || '',
      paid_to: full.paid_to || '',
      payment_method: full.payment_method || '',
      payment_source: full.payment_source || '',
      // Files stay `null` so the user can optionally REPLACE them. The
      // preview line below the input surfaces the existing filename.
      invoice: null,
      receipt: null,
      new_invoices: [],
      new_receipts: [],
      existing_invoice_files: invArr,
      existing_receipt_files: recArr,
      // Read-only meta shown as "already on file" pill in the form
      existing_invoice_url: full.invoice_url || '',
      existing_receipt_url: full.receipt_url || '',
    });
    setExpenseDialogOpen(true);
  };

  const handleStatusChange = async (candidateId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      await axios.put(`${API}/tap/candidates/${candidateId}/status`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Status updated');
      fetchCandidates();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleImportToRider = async (candidateId) => {
    try {
      const response = await axios.post(`${API}/tap/candidates/${candidateId}/import-to-rider`, {}, {
        headers: getAuthHeader()
      });
      
      // Show success message with details
      if (response.data.fixed_fields) {
        toast.success(response.data.message, { duration: 5000 });
      } else {
        const loanSync = response.data.loan_sync;
        if (loanSync) {
          toast.success(
            `${response.data.message}. Loan synced: ${loanSync.expenses_synced} entries, Outstanding: AED ${loanSync.loan_outstanding?.toLocaleString()}`,
            { duration: 5000 }
          );
        } else {
          toast.success(response.data.message);
        }
      }
      
      fetchCandidates();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to import to rider'));
    }
  };

  const handleDeleteCandidate = async (candidateId) => {
    if (!window.confirm('Are you sure you want to delete this candidate?')) return;
    try {
      await axios.delete(`${API}/tap/candidates/${candidateId}`, { headers: getAuthHeader() });
      toast.success('Candidate deleted');
      fetchCandidates();
    } catch (error) {
      toast.error('Failed to delete candidate');
    }
  };

  // Recalculate total expenses for all candidates
  const handleRecalculateExpenses = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/tap/recalculate-expenses`, {}, {
        headers: getAuthHeader()
      });
      toast.success(response.data.message);
      fetchCandidates(); // Refresh data
    } catch (error) {
      toast.error('Failed to recalculate expenses');
    } finally {
      setLoading(false);
    }
  };

  // Get expense subtypes based on selected type
  const getExpenseSubtypes = () => {
    switch (expenseForm.expense_type) {
      case 'visa': return config.visa_subtypes || [];
      case 'insurance': return config.insurance_subtypes || [];
      case 'driving_license': 
        // If driving_payment_type is selected, return subtypes for that payment type
        if (expenseForm.driving_payment_type && config.driving_license_config) {
          return config.driving_license_config[expenseForm.driving_payment_type]?.subtypes || [];
        }
        return [];
      default: return [];
    }
  };

  // Get driving license payment types
  const getDrivingPaymentTypes = () => {
    if (!config.driving_license_config) return [];
    return Object.entries(config.driving_license_config).map(([key, value]) => ({
      value: key,
      label: value.label
    }));
  };

  // Get payment sources based on method
  const getPaymentSources = () => {
    if (!config.payment_sources) return [];
    return config.payment_sources[expenseForm.payment_method] || [];
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">
            {isMobile ? 'TAP' : 'TAP - Talent Acquisition Platform'}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">New Hiring Database Management</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              setReadyOrphanScanRunning(true);
              setReadyOrphanScan(null);
              setReadyOrphanImportResult(null);
              setReadyOrphanDialogOpen(true);
              try {
                // include_all=true also surfaces "In Process" candidates
                // whose downstream records already exist (stage-out-of-sync).
                const res = await axios.get(
                  `${process.env.REACT_APP_BACKEND_URL}/api/tap/admin/ready-stage-orphans?include_all=true`,
                  { headers: getAuthHeader() }
                );
                setReadyOrphanScan(res.data);
              } catch (err) {
                toast.error('Scan failed: ' + (err?.response?.data?.detail || err.message));
              } finally {
                setReadyOrphanScanRunning(false);
              }
            }}
            className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-50"
            data-testid="scan-ready-orphans-btn"
          >
            <AlertCircle className="w-4 h-4" />
            Scan Ready Orphans
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkDLExcelDialogOpen(true)}
            className="gap-2 border-blue-200 text-blue-700 hover:bg-blue-50"
            data-testid="bulk-update-dl-excel-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Bulk Update DL Info (Excel)
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setBulkUploadDialogOpen(true)}
            className="gap-2 border-amber-200 text-amber-700 hover:bg-amber-50"
            data-testid="bulk-upload-docs-btn"
          >
            <FileUp className="w-4 h-4" />
            Bulk Upload Docs
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`flex gap-2 border-b pb-4 ${isMobile ? 'overflow-x-auto pb-3' : ''}`}>
        <Button
          variant={activeTab === 'enroll' ? 'default' : 'outline'}
          onClick={() => setActiveTab('enroll')}
          className="gap-2 whitespace-nowrap"
          size={isMobile ? 'sm' : 'default'}
        >
          <UserPlus className="w-4 h-4" />
          Enroll
        </Button>
        <Button
          variant={activeTab === 'expenses' ? 'default' : 'outline'}
          onClick={() => setActiveTab('expenses')}
          className="gap-2 whitespace-nowrap"
          size={isMobile ? 'sm' : 'default'}
        >
          <Receipt className="w-4 h-4" />
          Expenses
        </Button>
        <Button
          variant={activeTab === 'documents' ? 'default' : 'outline'}
          onClick={() => setActiveTab('documents')}
          className="gap-2 whitespace-nowrap"
          size={isMobile ? 'sm' : 'default'}
        >
          <FileUp className="w-4 h-4" />
          Create Documents
        </Button>
      </div>

      {/* Dashboard Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Card 
          className={`bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 cursor-pointer hover:shadow-md transition-all ${filters.status === 'all' ? 'ring-2 ring-blue-500 ring-offset-1' : ''}`}
          onClick={() => setFilters({...filters, status: 'all'})}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-blue-600" />
              <div>
                <p className="text-xs sm:text-sm text-blue-600">Total</p>
                <p className="text-xl sm:text-2xl font-bold text-blue-800">{candidates.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 cursor-pointer hover:shadow-md transition-all ${filters.status === 'in_process' ? 'ring-2 ring-amber-500 ring-offset-1' : ''}`}
          onClick={() => setFilters({...filters, status: 'in_process'})}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-amber-600" />
              <div>
                <p className="text-xs sm:text-sm text-amber-600">In Process</p>
                <p className="text-xl sm:text-2xl font-bold text-amber-800">
                  {candidates.filter(c => c.status === 'in_process').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className={`bg-gradient-to-br from-green-50 to-green-100 border-green-200 cursor-pointer hover:shadow-md transition-all ${filters.status === 'ready' ? 'ring-2 ring-green-500 ring-offset-1' : ''}`}
          onClick={() => setFilters({...filters, status: 'ready'})}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <CheckCircle2 className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" />
              <div>
                <p className="text-xs sm:text-sm text-green-600">Ready</p>
                <p className="text-xl sm:text-2xl font-bold text-green-800">
                  {candidates.filter(c => c.status === 'ready').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card 
          className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 cursor-pointer hover:shadow-md transition-all relative group"
          onClick={() => setActiveTab('expenses')}
        >
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600 flex items-center justify-center">
                <DirhamIcon size="1.5em" />
              </div>
              <div className="flex-1">
                <p className="text-xs sm:text-sm text-purple-600">Expenses</p>
                <p className="text-xl sm:text-2xl font-bold text-purple-800">
                  {candidates.reduce((sum, c) => sum + (c.total_expenses || 0), 0).toLocaleString()}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); handleRecalculateExpenses(); }}
                disabled={loading}
                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 h-auto"
                title="Recalculate all expense totals"
              >
                <RefreshCw className={`w-4 h-4 text-purple-600 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enroll Tab */}
      {activeTab === 'enroll' && (
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
                <UserPlus className="w-5 h-5" />
                {isMobile ? 'Candidates' : 'Candidate Enrollment'}
              </CardTitle>
              <CardDescription className="text-sm">Manage new candidate registrations</CardDescription>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant="outline"
                className="gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  try {
                    const resp = await axios.get(`${API}/tap/candidates/export.xlsx`, {
                      headers: getAuthHeader(),
                      responseType: 'blob',
                      timeout: 240000,
                    });
                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `tap_candidates_${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Excel download failed');
                  }
                }}
                data-testid="tap-export-xlsx"
                title="Download every TAP candidate with all details (including Source column) as an Excel workbook."
              >
                <Download className="w-4 h-4" />
                Excel
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-red-300 text-red-700 hover:bg-red-50"
                onClick={async () => {
                  try {
                    const resp = await axios.get(`${API}/tap/candidates/export.pdf`, {
                      headers: getAuthHeader(),
                      responseType: 'blob',
                      timeout: 240000,
                    });
                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `tap_candidates_${new Date().toISOString().slice(0, 10)}.pdf`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'PDF download failed');
                  }
                }}
                data-testid="tap-export-pdf"
                title="Download every TAP candidate as a landscape PDF (A3) with all details."
              >
                <Download className="w-4 h-4" />
                PDF
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                onClick={async () => {
                  try {
                    toast.info('Running document audit — this may take a moment…');
                    const resp = await axios.get(`${API}/admin/audit/tap-documents.xlsx`, {
                      headers: getAuthHeader(),
                      responseType: 'blob',
                      timeout: 240000,
                    });
                    const url = window.URL.createObjectURL(new Blob([resp.data]));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `tap_document_audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
                    document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                    toast.success('Document audit downloaded');
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Document audit failed');
                  }
                }}
                data-testid="tap-doc-audit-btn"
                title="Excel — per-candidate document checklist with Uploaded / Pending / Storage Location."
              >
                <AlertCircle className="w-4 h-4" />
                Document Audit
              </Button>
              <Button data-testid="open-enroll-dialog-btn" onClick={() => setEnrollDialogOpen(true)} className="gap-2 bg-green-600 hover:bg-green-700">
                <Plus className="w-4 h-4" />
                Enroll New Candidate
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, mobile, passport, nationality..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="candidate-search"
                />
              </div>
              {searchTerm && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSearchTerm('')}
                  className="text-slate-500"
                >
                  Clear
                </Button>
              )}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No candidates enrolled yet</p>
                <Button onClick={() => setEnrollDialogOpen(true)} className="mt-4 gap-2">
                  <Plus className="w-4 h-4" />
                  Enroll First Candidate
                </Button>
              </div>
            ) : (
              <div>
                <Table className="w-full table-fixed">
                  <TableHeader className="sticky top-0 bg-slate-50 z-10">
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-[7%]">Source</TableHead>
                      <TableHead className="w-[7%]">ID</TableHead>
                      <TableHead className="w-[13%]">Name</TableHead>
                      <TableHead className="w-[9%]">Nationality</TableHead>
                      <TableHead className="w-[9%]">UAE Mobile</TableHead>
                      <TableHead className="w-[9%]">Passport</TableHead>
                      <TableHead className="w-[9%]">Lessee</TableHead>
                      <TableHead className="w-[8%] text-center">Contract</TableHead>
                      <TableHead className="w-[7%] text-right">Advance</TableHead>
                      <TableHead className="w-[7%] text-center">Documents</TableHead>
                      <TableHead className="w-[7%] text-center">Status</TableHead>
                      <TableHead className="w-[8%] text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedEnrollCandidates.map(candidate => (
                      <TableRow 
                        key={candidate.id} 
                        className="cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => {
                          setSelectedCandidate(candidate);
                          setCandidateDetailsDialogOpen(true);
                          fetchAdvancePayments(candidate.id);
                        }}
                      >
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={candidate.source === 'Hiring'
                              ? 'text-[10px] uppercase bg-violet-50 text-violet-800 border-violet-300'
                              : 'text-[10px] uppercase bg-slate-50 text-slate-700 border-slate-300'}
                            title={candidate.source === 'Hiring' ? 'Imported from the Hiring pipeline' : 'Created directly via the TAP form'}
                          >
                            {candidate.source || 'TAP'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-blue-600 text-sm">{candidate.employee_unique_id || '-'}</TableCell>
                        <TableCell className="font-medium truncate" title={candidate.name}>{truncateName(candidate.name)}</TableCell>
                        <TableCell className="truncate">{candidate.nationality || '-'}</TableCell>
                        <TableCell className="truncate">{candidate.uae_mobile || '-'}</TableCell>
                        <TableCell className="truncate">{candidate.passport_number || candidate.passport_no || '-'}</TableCell>
                        <TableCell className="truncate" title={candidate.lessee_name}>{truncateCompanyName(candidate.lessee_name) || '-'}</TableCell>
                        <TableCell className="text-center"><CurrencyDisplay amount={candidate.loan_contract_value || 0} /></TableCell>
                        <TableCell className="text-right"><CurrencyDisplay amount={candidate.advance_payment || 0} /></TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          {(candidate.has_documents || candidate.passport_copy_url || candidate.passport_photo_url || 
                            candidate.home_license_url || candidate.home_id_card_url ||
                            candidate.employment_agreement_url || candidate.loan_agreement_url ||
                            candidate.receipt_cash_paid_url || candidate.emirates_id_doc_url || candidate.visa_doc_url) ? (
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="gap-1 text-blue-600 border-blue-200"
                              onClick={async () => {
                                const fullData = await fetchFullCandidate(candidate.id);
                                setSelectedCandidate(fullData || candidate);
                                setDocumentsDialogOpen(true);
                              }}
                            >
                              <Download className="w-3 h-3" />
                              View
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-xs">No docs</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge 
                            variant="outline"
                            className={
                              candidate.imported_to_rider
                                ? 'bg-blue-100 text-blue-700'
                                : candidate.status === 'ready' 
                                  ? 'bg-green-100 text-green-700' 
                                  : candidate.status === 'on_hold'
                                    ? 'bg-red-100 text-red-700'
                                    : candidate.status === 'cyclist'
                                      ? 'bg-purple-100 text-purple-700'
                                      : 'bg-amber-100 text-amber-700'
                            }
                          >
                            {candidate.imported_to_rider 
                              ? 'Exported' 
                              : candidate.status === 'ready' 
                                ? 'Ready' 
                                : candidate.status === 'on_hold' 
                                  ? 'On Hold' 
                                  : candidate.status === 'cyclist' 
                                    ? 'Cyclist' 
                                    : 'In Process'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`actions-menu-${candidate.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedCandidate(candidate);
                                  setEditCandidateDialogOpen(true);
                                  fetchAdvancePayments(candidate.id);
                                }}
                                className="cursor-pointer"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Candidate
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteCandidate(candidate.id)}
                                className="cursor-pointer text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete Candidate
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <Pagination
                  currentPage={enrollPage}
                  totalPages={Math.ceil(filteredEnrollCandidates.length / CANDIDATES_PER_PAGE)}
                  totalCount={filteredEnrollCandidates.length}
                  itemsPerPage={CANDIDATES_PER_PAGE}
                  onPageChange={setEnrollPage}
                  loading={loading}
                />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Expenses Tab */}
      {activeTab === 'expenses' && (
        <Card>
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5" />
                Expense Tracking
              </CardTitle>
              <CardDescription>Track expenses for each candidate</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExpensesExportExcel}
                className="gap-2"
                data-testid="expenses-export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExpensesExportPDF}
                className="gap-2"
                data-testid="expenses-export-pdf-btn"
              >
                <FileText className="w-4 h-4" />
                Export PDF
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by name, lessee, status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="expenses-search"
                />
              </div>
              {searchTerm && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSearchTerm('')}
                  className="text-slate-500"
                >
                  Clear
                </Button>
              )}
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>No candidates to track expenses</p>
              </div>
            ) : (
              <div className="rounded-lg border border-slate-200">
              <Table className="w-full">
                <TableHeader className="sticky top-0 z-10">
                  <TableRow className="bg-gradient-to-r from-slate-100 to-slate-50 border-b-2 border-slate-200">
                    <TableHead className="w-[30%] py-4 text-slate-700 font-semibold uppercase text-xs tracking-wider">Candidate Name</TableHead>
                    <TableHead className="w-[25%] py-4 text-slate-700 font-semibold uppercase text-xs tracking-wider">Lessee Company</TableHead>
                    <TableHead className="w-[14%] py-4 text-right text-slate-700 font-semibold uppercase text-xs tracking-wider text-center">Loan Contract Amount</TableHead>
                    <TableHead className="w-[14%] py-4 text-right text-slate-700 font-semibold uppercase text-xs tracking-wider">Advance Payment</TableHead>
                    <TableHead className="w-[14%] py-4 text-right text-slate-700 font-semibold uppercase text-xs tracking-wider">Total Spent</TableHead>
                    <TableHead className="w-[9%] py-4 text-center text-slate-700 font-semibold uppercase text-xs tracking-wider">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedExpenseCandidates.map((candidate, index) => (
                    <TableRow 
                      key={candidate.id} 
                      className={`hover:bg-slate-50 transition-colors cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}
                      onClick={() => {
                        setSelectedCandidate(candidate);
                        setExpenseListDialogOpen(true);
                      }}
                      data-testid={`expense-row-${candidate.id}`}
                    >
                      <TableCell className="py-4">
                        <div>
                          <p className="font-medium text-slate-800" title={candidate.name}>{candidate.name}</p>
                          <p className="text-xs text-slate-500">
                            {candidate.employee_unique_id || 'No ID'}
                            {candidate.latest_expense_date && (
                              <span className="ml-2 text-slate-400" title={`${candidate.expense_count || 0} expense(s) recorded`}>
                                · Last exp: {candidate.latest_expense_date}
                              </span>
                            )}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="py-4">
                        <p className="text-slate-700" title={candidate.lessee_name}>{candidate.lessee_name || '-'}</p>
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 font-medium">
                          <CurrencyDisplay amount={candidate.loan_contract_value || 0} />
                        </span>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
                          <CurrencyDisplay amount={candidate.advance_payment || 0} />
                        </span>
                      </TableCell>
                      <TableCell className="py-4 text-right">
                        <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-50 text-slate-700 font-medium">
                          <CurrencyDisplay amount={candidate.total_expenses || 0} />
                        </span>
                      </TableCell>
                      <TableCell className="py-4 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8 w-8 p-0 rounded-full hover:bg-slate-100">
                              <MoreVertical className="w-4 h-4 text-slate-600" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setExpenseDialogOpen(true);
                              }}
                              className="cursor-pointer"
                            >
                              <Plus className="w-4 h-4 mr-2 text-green-600" />
                              Add Expense
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setExpenseListDialogOpen(true);
                              }}
                              className="cursor-pointer"
                              data-testid={`edit-expenses-${candidate.id}`}
                            >
                              <Edit className="w-4 h-4 mr-2 text-amber-600" />
                              Edit Expenses
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setExpenseListDialogOpen(true);
                              }}
                              className="cursor-pointer"
                            >
                              <Eye className="w-4 h-4 mr-2 text-blue-600" />
                              View Expenses
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination
                currentPage={expensesPage}
                totalPages={Math.ceil(filteredExpenseCandidates.length / CANDIDATES_PER_PAGE)}
                totalCount={filteredExpenseCandidates.length}
                itemsPerPage={CANDIDATES_PER_PAGE}
                onPageChange={setExpensesPage}
                loading={loading}
              />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <CreateDocuments user={user} />
      )}


      {/* Ready-stage Orphan Scanner Dialog */}
      <Dialog open={readyOrphanDialogOpen} onOpenChange={(o) => {
        setReadyOrphanDialogOpen(o);
        if (!o) { setReadyOrphanScan(null); setReadyOrphanImportResult(null); }
      }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-purple-600" />
              Ready-stage Orphan Scanner
            </DialogTitle>
            <DialogDescription>
              Finds every TAP candidate whose stage badge reads <strong>Ready</strong> (all documents
              uploaded, or status = ready/cyclist) but who was never manually imported into Talent PMS
              &amp; Riders. Matches by employee ID, Emirates ID, and passport across all downstream realms.
            </DialogDescription>
          </DialogHeader>

          {readyOrphanScanRunning && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Scanning candidates…
            </div>
          )}

          {readyOrphanScan && !readyOrphanImportResult && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                  Scanned: {readyOrphanScan.scanned}
                </Badge>
                <Badge
                  variant="outline"
                  className={`${readyOrphanScan.orphan_count > 0 ? 'bg-amber-50 text-amber-800 border-amber-200' : 'bg-green-50 text-green-800 border-green-200'}`}
                  data-testid="orphan-count-badge"
                >
                  Orphans (safe to import): {readyOrphanScan.orphan_count}
                </Badge>
                <Badge variant="outline" className="bg-green-50 text-green-800 border-green-200">
                  Already promoted: {readyOrphanScan.already_present_count || 0}
                </Badge>
                <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200">
                  Stage-out-of-sync (In Process on TAP but present downstream): {readyOrphanScan.in_process_with_downstream_count || 0}
                </Badge>
              </div>

              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2 leading-relaxed">
                <strong>Duplicate safety:</strong> This tool uses the same identity resolver as the single-candidate import guard
                — matches by Emirates ID, passport, and normalized name across Riders + Talent PMS + Staff. A second identity
                re-check runs right before each import, so anyone already promoted is skipped, not duplicated.
              </div>

              {readyOrphanScan.orphan_count === 0 && readyOrphanScan.in_process_with_downstream_count === 0 ? (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded p-3">
                  ✓ Everything is in sync. No orphans and no stage-mismatches detected.
                </div>
              ) : (
                <>
                  {readyOrphanScan.orphan_count > 0 && (
                    <div>
                      <div className="text-sm font-medium text-amber-800 mb-1">Ready-stage orphans (will be imported):</div>
                      <div className="max-h-[300px] overflow-y-auto border rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 border-b">EID / Passport</th>
                              <th className="text-left p-2 border-b">Employee ID</th>
                              <th className="text-left p-2 border-b">Name</th>
                              <th className="text-left p-2 border-b">Lessee</th>
                              <th className="text-left p-2 border-b">Why Stuck</th>
                            </tr>
                          </thead>
                          <tbody>
                            {readyOrphanScan.orphans.map((o) => (
                              <tr key={o.candidate_id} className="border-b hover:bg-slate-50">
                                <td className="p-2 font-mono">{o.emirates_id || o.passport_number || '—'}</td>
                                <td className="p-2 font-mono">{o.employee_unique_id || '—'}</td>
                                <td className="p-2 font-medium">{o.name}</td>
                                <td className="p-2 text-slate-600">{o.lessee_name || '—'}</td>
                                <td className="p-2">
                                  <Badge variant="outline" className={`text-[10px] ${
                                    o.reason === 'status_ready_never_imported' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                                    o.reason === 'docs_uploaded_status_still_in_process' ? 'bg-blue-50 text-blue-800 border-blue-200' :
                                    'bg-slate-50 text-slate-700 border-slate-200'
                                  }`}>
                                    {o.reason.replaceAll('_',' ')}
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {readyOrphanScan.in_process_with_downstream_count > 0 && (
                    <div>
                      <div className="text-sm font-medium text-blue-800 mb-1">
                        Stage-out-of-sync (In Process on TAP but exists on Talent/Rider — NOT imported, no duplicate risk):
                      </div>
                      <div className="max-h-[200px] overflow-y-auto border rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left p-2 border-b">Employee ID</th>
                              <th className="text-left p-2 border-b">Name</th>
                              <th className="text-left p-2 border-b">TAP Status</th>
                              <th className="text-left p-2 border-b">Matched In</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(readyOrphanScan.in_process_with_downstream_sample || []).map((r) => (
                              <tr key={r.candidate_id} className="border-b hover:bg-slate-50">
                                <td className="p-2 font-mono">{r.employee_unique_id || '—'}</td>
                                <td className="p-2 font-medium">{r.name}</td>
                                <td className="p-2 text-slate-600">{r.status || 'in_process'}</td>
                                <td className="p-2">
                                  <Badge variant="outline" className="text-[10px] bg-green-50 text-green-800 border-green-200">
                                    {r.matched_in} ({r.match_reason.replaceAll('_',' ')})
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {readyOrphanImportResult && (
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-green-100 text-green-800">✓ Imported: {readyOrphanImportResult.imported_count}</Badge>
                <Badge className="bg-blue-100 text-blue-800">⏭ Skipped (dedup): {readyOrphanImportResult.skipped_count || 0}</Badge>
                <Badge className="bg-rose-100 text-rose-800">✗ Failed: {readyOrphanImportResult.failed_count}</Badge>
              </div>
              {readyOrphanImportResult.imported_count > 0 && (
                <div className="max-h-40 overflow-y-auto text-xs bg-green-50 border border-green-100 rounded p-2">
                  {readyOrphanImportResult.imported.map((r) => (
                    <div key={r.candidate_id} className="py-1">
                      ✓ {r.name} ({r.employee_unique_id}) → rider {r.rider_unique_id || r.rider_id}
                    </div>
                  ))}
                </div>
              )}
              {(readyOrphanImportResult.skipped_count || 0) > 0 && (
                <div className="max-h-32 overflow-y-auto text-xs bg-blue-50 border border-blue-100 rounded p-2">
                  {(readyOrphanImportResult.skipped || []).map((r, i) => (
                    <div key={i} className="py-1">
                      ⏭ {r.name || r.candidate_id}: <span className="opacity-80">{r.reason}</span>
                    </div>
                  ))}
                </div>
              )}
              {readyOrphanImportResult.failed_count > 0 && (
                <div className="max-h-32 overflow-y-auto text-xs bg-rose-50 border border-rose-100 rounded p-2">
                  {readyOrphanImportResult.failed.map((r, i) => (
                    <div key={i} className="py-1">
                      ✗ {r.name || r.candidate_id}: <span className="opacity-80">{r.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReadyOrphanDialogOpen(false)}>Close</Button>
            {readyOrphanScan && readyOrphanScan.orphan_count > 0 && !readyOrphanImportResult && (
              <Button
                disabled={readyOrphanImporting}
                onClick={async () => {
                  if (!window.confirm(`Import ${readyOrphanScan.orphan_count} orphan candidate(s) into Talent PMS + Riders? This will also create loan accounts and cannot be undone.`)) return;
                  setReadyOrphanImporting(true);
                  try {
                    const res = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/tap/admin/ready-stage-orphans/import`,
                      {},
                      { headers: getAuthHeader() }
                    );
                    setReadyOrphanImportResult(res.data);
                    toast.success(`Imported ${res.data.imported_count} orphans (${res.data.failed_count} failed)`);
                    fetchCandidates();
                  } catch (err) {
                    toast.error('Import failed: ' + (err?.response?.data?.detail || err.message));
                  } finally {
                    setReadyOrphanImporting(false);
                  }
                }}
                className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="import-ready-orphans-btn"
              >
                {readyOrphanImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                Import All {readyOrphanScan.orphan_count} Orphans
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update DL Info via Excel Dialog */}
      <Dialog open={bulkDLExcelDialogOpen} onOpenChange={(o) => {
        setBulkDLExcelDialogOpen(o);
        if (!o) { setBulkDLExcelFile(null); setBulkDLExcelResults(null); }
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              Bulk Update Driving License Info
            </DialogTitle>
            <DialogDescription>
              Upload an Excel sheet with columns: <strong>Emirates ID</strong>, <strong>Traffic file no.</strong>,{' '}
              <strong>Driving License No.</strong>, <strong>Issue date</strong>, <strong>Expiry date</strong>.
              Each row is matched to a candidate by Emirates ID and updates the DL fields on the view-details page.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Excel file (.xlsx)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setBulkDLExcelFile(e.target.files?.[0] || null)}
                data-testid="bulk-dl-excel-input"
              />
              {bulkDLExcelFile && (
                <p className="text-xs text-slate-500">{bulkDLExcelFile.name} · {Math.round(bulkDLExcelFile.size / 1024)} KB</p>
              )}
            </div>

            {bulkDLExcelResults && (
              <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                <div className="flex items-center gap-3 text-sm flex-wrap">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    ✓ {bulkDLExcelResults.updated} updated
                  </Badge>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    ? {bulkDLExcelResults.no_match} unmatched
                  </Badge>
                  <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                    – {bulkDLExcelResults.skipped} skipped
                  </Badge>
                </div>
                <div className="max-h-64 overflow-y-auto text-xs space-y-1">
                  {(bulkDLExcelResults.results || []).map((r, i) => (
                    <div key={i} className={`p-2 rounded border ${
                      r.status === 'updated' ? 'bg-green-50 border-green-100 text-green-800' :
                      r.status === 'no_match' ? 'bg-amber-50 border-amber-100 text-amber-800' :
                      'bg-slate-50 border-slate-100 text-slate-700'
                    }`}>
                      <div className="font-mono">Row {r.row}{r.emirates_id ? ` · EID ${r.emirates_id}` : ''}</div>
                      <div className="text-[11px] opacity-80">
                        {r.status === 'updated' && <>→ {r.candidate_name} ({r.employee_unique_id}) · updated {(r.updated_fields || []).join(', ')}</>}
                        {r.status === 'no_match' && <>{r.reason}</>}
                        {r.status === 'skipped' && <>{r.reason}</>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDLExcelDialogOpen(false)}>Close</Button>
            <Button
              disabled={!bulkDLExcelFile || bulkDLExcelRunning}
              onClick={async () => {
                setBulkDLExcelRunning(true);
                setBulkDLExcelResults(null);
                try {
                  const fd = new FormData();
                  fd.append('file', bulkDLExcelFile);
                  const res = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/tap/driving-license/bulk-update-excel`,
                    fd,
                    { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                  );
                  setBulkDLExcelResults(res.data);
                  toast.success(`Bulk DL update complete — ${res.data.updated} updated, ${res.data.no_match} unmatched`);
                  fetchCandidates();
                } catch (err) {
                  toast.error('Bulk update failed: ' + (err?.response?.data?.detail || err.message));
                } finally {
                  setBulkDLExcelRunning(false);
                }
              }}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="bulk-dl-excel-submit-btn"
            >
              {bulkDLExcelRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />}
              Upload &amp; Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Docs Dialog — matches uploaded files to candidates
          by filename containing UID / Emirates ID / Passport, then
          pushes each file to a per-candidate SharePoint folder. */}
      <Dialog open={bulkUploadDialogOpen} onOpenChange={(o) => {
        setBulkUploadDialogOpen(o);
        if (!o) { setBulkUploadFiles([]); setBulkUploadResults(null); }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-amber-600" />
              Bulk Upload Candidate Documents
            </DialogTitle>
            <DialogDescription>
              Upload multiple files at once. Each file is auto-matched to a candidate by scanning its
              filename for the candidate&apos;s UID (e.g. GEDS0344), Emirates ID (e.g. 784-1990-1234567-1) or
              Passport number. Files land in each candidate&apos;s own SharePoint folder.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document type</Label>
              <Select value={bulkUploadDocType} onValueChange={setBulkUploadDocType}>
                <SelectTrigger data-testid="bulk-upload-doc-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="loan_agreement">Loan Agreement</SelectItem>
                  <SelectItem value="employment_agreement">Employment Agreement</SelectItem>
                  <SelectItem value="passport_copy">Passport Copy</SelectItem>
                  <SelectItem value="emirates_id_doc">Emirates ID</SelectItem>
                  <SelectItem value="visa_doc">Visa</SelectItem>
                  <SelectItem value="medical_report">Medical Report</SelectItem>
                  <SelectItem value="uae_driving_license">UAE Driving License</SelectItem>
                  <SelectItem value="operational_health_certificate">Operational Health Certificate (OHC)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select files (PDF or images) — filename must contain UID / EID / Passport</Label>
              <Input
                type="file"
                multiple
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setBulkUploadFiles(Array.from(e.target.files || []))}
                data-testid="bulk-upload-files-input"
              />
              {bulkUploadFiles.length > 0 && (
                <p className="text-xs text-slate-500">
                  {bulkUploadFiles.length} file{bulkUploadFiles.length === 1 ? '' : 's'} selected
                </p>
              )}
            </div>

            {/* Batch progress — files chunk 10 per request so large
                uploads (>50 files) never hit the 120s proxy timeout. */}
            {bulkUploadRunning && bulkUploadProgress.total > 0 && (
              <div className="space-y-1" data-testid="bulk-upload-progress">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-slate-600">
                    Uploading batch {Math.ceil(bulkUploadProgress.done / 10)} / {Math.ceil(bulkUploadProgress.total / 10)}
                  </span>
                  <span className="text-slate-800 font-medium">
                    {bulkUploadProgress.done} / {bulkUploadProgress.total}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all duration-300"
                    style={{ width: `${(bulkUploadProgress.done / bulkUploadProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {bulkUploadResults && (
              <div className="border rounded-lg p-3 bg-slate-50 space-y-2">
                <div className="flex items-center gap-3 text-sm">
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    ✓ {bulkUploadResults.uploaded} uploaded
                  </Badge>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                    ? {bulkUploadResults.no_match} unmatched
                  </Badge>
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                    ✕ {bulkUploadResults.failed} failed
                  </Badge>
                </div>
                <div className="max-h-64 overflow-y-auto text-xs space-y-1">
                  {(bulkUploadResults.results || []).map((r, i) => (
                    <div key={i} className={`p-2 rounded border ${
                      r.status === 'uploaded' ? 'bg-green-50 border-green-100 text-green-800' :
                      r.status === 'no_match' ? 'bg-amber-50 border-amber-100 text-amber-800' :
                      'bg-red-50 border-red-100 text-red-800'
                    }`}>
                      <div className="font-mono truncate">{r.filename}</div>
                      <div className="text-[11px] opacity-80">
                        {r.status === 'uploaded' && <>→ {r.candidate_name} ({r.employee_unique_id}) · matched by {r.matched_by}</>}
                        {r.status === 'no_match' && <>{r.reason}</>}
                        {(r.status === 'upload_failed' || r.status === 'error') && <>{r.reason || 'Upload failed'}</>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUploadDialogOpen(false)}>Close</Button>
            <Button
              disabled={!bulkUploadFiles.length || bulkUploadRunning}
              onClick={async () => {
                setBulkUploadRunning(true);
                setBulkUploadResults(null);
                const CHUNK_SIZE = 10; // 10 files per request keeps
                // each round-trip under the 120s proxy read timeout
                // even when SharePoint responds slowly per file.
                const total = bulkUploadFiles.length;
                setBulkUploadProgress({ done: 0, total });
                const aggregated = { uploaded: 0, no_match: 0, failed: 0, results: [], document_type: bulkUploadDocType, total_files: total };
                try {
                  for (let i = 0; i < total; i += CHUNK_SIZE) {
                    const chunk = bulkUploadFiles.slice(i, i + CHUNK_SIZE);
                    const fd = new FormData();
                    fd.append('document_type', bulkUploadDocType);
                    chunk.forEach(f => fd.append('files', f));
                    const res = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/tap/documents/bulk-upload`,
                      fd,
                      { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
                    );
                    aggregated.uploaded += res.data.uploaded || 0;
                    aggregated.no_match += res.data.no_match || 0;
                    aggregated.failed += res.data.failed || 0;
                    aggregated.results.push(...(res.data.results || []));
                    setBulkUploadProgress({ done: Math.min(i + chunk.length, total), total });
                    // Show partial results as they arrive so operators
                    // can spot problems without waiting for the whole run.
                    setBulkUploadResults({ ...aggregated });
                  }
                  toast.success(`Bulk upload complete — ${aggregated.uploaded} uploaded, ${aggregated.no_match} unmatched, ${aggregated.failed} failed`);
                } catch (err) {
                  toast.error('Bulk upload failed: ' + (err?.response?.data?.detail || err.message));
                  // Preserve whatever partial progress we made.
                  setBulkUploadResults({ ...aggregated, _partial_error: err?.message || 'network error' });
                } finally {
                  setBulkUploadRunning(false);
                }
              }}
              className="gap-2 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="bulk-upload-submit-btn"
            >
              {bulkUploadRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              Upload {bulkUploadFiles.length || ''} file{bulkUploadFiles.length === 1 ? '' : 's'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enroll Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Enroll New Candidate
            </DialogTitle>
            <DialogDescription>Enter candidate information for enrollment</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Basic Information
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    data-testid="enroll-name"
                    value={enrollForm.name}
                    onChange={(e) => setEnrollForm({...enrollForm, name: e.target.value})}
                    placeholder="Full name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nationality</Label>
                  <Input
                    value={enrollForm.nationality}
                    onChange={(e) => setEnrollForm({...enrollForm, nationality: e.target.value})}
                    placeholder="e.g., Indian"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={enrollForm.date_of_birth}
                    onChange={(e) => setEnrollForm({...enrollForm, date_of_birth: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Lessee Company</Label>
                  <Select
                    value={enrollForm.lessee_id}
                    onValueChange={(value) => {
                      const lessee = lessees.find(l => l.id === value);
                      setEnrollForm({
                        ...enrollForm,
                        lessee_id: value,
                        lessee_name: lessee?.company_name || ''
                      });
                    }}
                  >
                    <SelectTrigger><SelectValue placeholder="Select lessee" /></SelectTrigger>
                    <SelectContent>
                      {lessees.map(l => (
                        <SelectItem key={l.id} value={l.id}>{l.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Home Address</Label>
                <Textarea
                  value={enrollForm.home_address}
                  onChange={(e) => setEnrollForm({...enrollForm, home_address: e.target.value})}
                  placeholder="Full home address"
                  rows={2}
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Contact Information
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>UAE Mobile No.</Label>
                  <Input
                    value={enrollForm.uae_mobile}
                    onChange={(e) => setEnrollForm({...enrollForm, uae_mobile: e.target.value})}
                    placeholder="+971 XX XXX XXXX"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Home Country Mobile No.</Label>
                  <Input
                    value={enrollForm.home_country_mobile}
                    onChange={(e) => setEnrollForm({...enrollForm, home_country_mobile: e.target.value})}
                    placeholder="With country code"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={enrollForm.email}
                    onChange={(e) => setEnrollForm({...enrollForm, email: e.target.value})}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>

            {/* References */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Home Country References
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="border rounded-lg p-3 space-y-3">
                  <Label className="font-medium">Reference 1</Label>
                  <Input
                    value={enrollForm.ref1_name}
                    onChange={(e) => setEnrollForm({...enrollForm, ref1_name: e.target.value})}
                    placeholder="Name"
                  />
                  <Input
                    value={enrollForm.ref1_mobile}
                    onChange={(e) => setEnrollForm({...enrollForm, ref1_mobile: e.target.value})}
                    placeholder="Mobile No."
                  />
                  <Input
                    value={enrollForm.ref1_address}
                    onChange={(e) => setEnrollForm({...enrollForm, ref1_address: e.target.value})}
                    placeholder="Address"
                  />
                </div>
                <div className="border rounded-lg p-3 space-y-3">
                  <Label className="font-medium">Reference 2</Label>
                  <Input
                    value={enrollForm.ref2_name}
                    onChange={(e) => setEnrollForm({...enrollForm, ref2_name: e.target.value})}
                    placeholder="Name"
                  />
                  <Input
                    value={enrollForm.ref2_mobile}
                    onChange={(e) => setEnrollForm({...enrollForm, ref2_mobile: e.target.value})}
                    placeholder="Mobile No."
                  />
                  <Input
                    value={enrollForm.ref2_address}
                    onChange={(e) => setEnrollForm({...enrollForm, ref2_address: e.target.value})}
                    placeholder="Address"
                  />
                </div>
              </div>
            </div>

            {/* Documents */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Documents
              </h4>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Home Country License No.</Label>
                  <Input
                    value={enrollForm.license_number}
                    onChange={(e) => setEnrollForm({...enrollForm, license_number: e.target.value})}
                    placeholder="License number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>License Issue Date</Label>
                  <Input
                    type="date"
                    value={enrollForm.license_issue_date}
                    onChange={(e) => setEnrollForm({...enrollForm, license_issue_date: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>License Expiry Date</Label>
                  <Input
                    type="date"
                    value={enrollForm.license_expiry_date}
                    onChange={(e) => setEnrollForm({...enrollForm, license_expiry_date: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Home Country ID Card</Label>
                  <Input
                    value={enrollForm.home_country_id_card}
                    onChange={(e) => setEnrollForm({...enrollForm, home_country_id_card: e.target.value})}
                    placeholder="ID card number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Passport No.</Label>
                  <Input
                    data-testid="enroll-passport-number"
                    value={enrollForm.passport_number}
                    onChange={(e) => {
                      setEnrollForm({...enrollForm, passport_number: e.target.value});
                      debouncedPassportCheck(e.target.value, null, false);
                    }}
                    placeholder="Passport number"
                    className={!passportValidation.valid ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  {passportValidation.checking && (
                    <p className="text-xs text-slate-500 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Checking...
                    </p>
                  )}
                  {!passportValidation.valid && passportValidation.error && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {passportValidation.error}
                    </p>
                  )}
                  {passportValidation.valid && enrollForm.passport_number && enrollForm.passport_number.length >= 3 && !passportValidation.checking && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Passport number is available
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Advance Payment & Loan Contract Value */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <DirhamIcon size="1em" />
                Advance Payment & Contract
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Advance Payment</Label>
                  <Input
                    type="number"
                    value={enrollForm.advance_payment}
                    onChange={(e) => setEnrollForm({...enrollForm, advance_payment: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Loan Contract Amount</Label>
                  <Input
                    type="number"
                    value={enrollForm.loan_contract_value}
                    onChange={(e) => setEnrollForm({...enrollForm, loan_contract_value: e.target.value})}
                    placeholder="0.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label>No. of Installments</Label>
                  <Input
                    type="number"
                    min="1"
                    value={enrollForm.no_of_installments}
                    onChange={(e) => setEnrollForm({...enrollForm, no_of_installments: e.target.value})}
                    placeholder="e.g. 12"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Installment Amount (AED)</Label>
                  <div className="flex items-center h-10 px-3 border rounded-md bg-slate-50 text-sm font-semibold text-blue-700">
                    {parseFloat(enrollForm.loan_contract_value) > 0 && parseInt(enrollForm.no_of_installments) > 0
                      ? (parseFloat(enrollForm.loan_contract_value) / parseInt(enrollForm.no_of_installments)).toFixed(2)
                      : '0.00'}
                  </div>
                </div>
              </div>
            </div>

            {/* Document Uploads */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Document Uploads
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Passport Copy <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="enroll-passport-copy"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, passport_copy: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.passport_copy && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.passport_copy.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1">
                    Passport Size Photo <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    data-testid="enroll-passport-photo"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, passport_photo: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.passport_photo && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.passport_photo.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Home Country License</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, home_license: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.home_license && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.home_license.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Home Country ID Card</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, home_id_card: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.home_id_card && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.home_id_card.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Employment Agreement</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, employment_agreement: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.employment_agreement && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.employment_agreement.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Loan Agreement</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, loan_agreement: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.loan_agreement && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.loan_agreement.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Receipt of Cash Paid</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, receipt_cash_paid: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.receipt_cash_paid && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.receipt_cash_paid.name}
                    </p>
                  )}
                </div>
              </div>
              
              <p className="text-sm font-medium text-slate-700 mt-4 mb-2">Additional Documents (Optional)</p>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>MB MOHRE</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, mb_mohre_unsigned: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.mb_mohre_unsigned && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.mb_mohre_unsigned.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>ST MOHRE</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, st_mohre_unsigned: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.st_mohre_unsigned && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.st_mohre_unsigned.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Signed MB MOHRE</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, mb_mohre: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.mb_mohre && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.mb_mohre.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Signed ST MOHRE</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, st_mohre: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.st_mohre && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.st_mohre.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Entry Permit</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, entry_permit: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.entry_permit && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.entry_permit.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Medical Report</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, medical_report: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.medical_report && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.medical_report.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Emirates ID</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, emirates_id_doc: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.emirates_id_doc && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.emirates_id_doc.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Visa</Label>
                  <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setEnrollForm({...enrollForm, visa_doc: e.target.files[0]})}
                    className="cursor-pointer"
                  />
                  {enrollForm.visa_doc && (
                    <p className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> {enrollForm.visa_doc.name}
                    </p>
                  )}
                </div>
              </div>
              <p className="text-xs text-slate-500">Accepted formats: PDF, JPG, JPEG, PNG</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>Cancel</Button>
            <Button data-testid="enroll-submit-btn" onClick={() => handleEnrollSubmit(false)} disabled={loading} className="gap-2 bg-green-600 hover:bg-green-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
              Enroll Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Expense Dialog */}
      <Dialog
        open={expenseDialogOpen}
        onOpenChange={(open) => {
          setExpenseDialogOpen(open);
          if (!open) setEditingExpenseId(null);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5" />
              {editingExpenseId ? 'Edit' : 'Add'} Expense - {selectedCandidate?.name}
            </DialogTitle>
            <DialogDescription>{editingExpenseId ? 'Amend any field or replace the uploaded invoice / receipt.' : 'Record expense for this candidate'}</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Expense Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type of Expense *</Label>
                <Select
                  value={expenseForm.expense_type}
                  onValueChange={(value) => setExpenseForm({...expenseForm, expense_type: value, expense_subtype: '', driving_payment_type: ''})}
                >
                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visa">Visa</SelectItem>
                    <SelectItem value="insurance">Insurance</SelectItem>
                    <SelectItem value="driving_license">Driving License</SelectItem>
                    <SelectItem value="accommodation">Accommodation</SelectItem>
                    <SelectItem value="miscellaneous">Miscellaneous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Driving License Payment Type Selection */}
              {expenseForm.expense_type === 'driving_license' && (
                <div className="space-y-2">
                  <Label>Payment Type *</Label>
                  <Select
                    value={expenseForm.driving_payment_type}
                    onValueChange={(value) => setExpenseForm({...expenseForm, driving_payment_type: value, expense_subtype: ''})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select payment type" /></SelectTrigger>
                    <SelectContent>
                      {getDrivingPaymentTypes().map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Subtype dropdown for visa, insurance */}
              {['visa', 'insurance'].includes(expenseForm.expense_type) && (
                <div className="space-y-2">
                  <Label>Sub-type *</Label>
                  <Select
                    value={expenseForm.expense_subtype}
                    onValueChange={(value) => setExpenseForm({...expenseForm, expense_subtype: value})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select sub-type" /></SelectTrigger>
                    <SelectContent>
                      {getExpenseSubtypes().map(subtype => (
                        <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Subtype dropdown for driving_license (after payment type selected) */}
              {expenseForm.expense_type === 'driving_license' && expenseForm.driving_payment_type && (
                <div className="space-y-2">
                  <Label>Expense Sub-type *</Label>
                  <Select
                    value={expenseForm.expense_subtype}
                    onValueChange={(value) => setExpenseForm({...expenseForm, expense_subtype: value})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select sub-type" /></SelectTrigger>
                    <SelectContent>
                      {getExpenseSubtypes().map(subtype => (
                        <SelectItem key={subtype} value={subtype}>{subtype}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Driving License specific fields */}
            {expenseForm.expense_type === 'driving_license' && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-blue-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Driving School Name</Label>
                  <Popover open={drivingSchoolPopoverOpen} onOpenChange={setDrivingSchoolPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={drivingSchoolPopoverOpen}
                        className="w-full justify-between h-9 font-normal"
                      >
                        {expenseForm.driving_school_name || "Select driving school..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search driving school..." />
                        <CommandList>
                          <CommandEmpty>No driving school found.</CommandEmpty>
                          <CommandGroup>
                            {drivingSchools.map((school) => (
                              <CommandItem
                                key={school.id}
                                value={school.name}
                                onSelect={(value) => {
                                  setExpenseForm({...expenseForm, driving_school_name: value});
                                  setDrivingSchoolPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    expenseForm.driving_school_name === school.name ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div>
                                  <p className="font-medium">{school.name}</p>
                                  {school.city && <p className="text-xs text-slate-500">{school.city}</p>}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                          <CommandGroup>
                            <CommandItem
                              onSelect={() => {
                                setDrivingSchoolPopoverOpen(false);
                                setAddDrivingSchoolDialogOpen(true);
                              }}
                              className="text-blue-600"
                            >
                              <Plus className="mr-2 h-4 w-4" />
                              Add New Driving School
                            </CommandItem>
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Date of Joining</Label>
                  <Input
                    type="date"
                    value={expenseForm.driving_school_joining_date}
                    onChange={(e) => setExpenseForm({...expenseForm, driving_school_joining_date: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Accommodation specific fields */}
            {expenseForm.expense_type === 'accommodation' && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50 rounded-lg">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={expenseForm.accommodation_location}
                    onChange={(e) => setExpenseForm({...expenseForm, accommodation_location: e.target.value})}
                    placeholder="Accommodation location"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Month & Year</Label>
                  <Input
                    type="month"
                    value={expenseForm.accommodation_month_year}
                    onChange={(e) => setExpenseForm({...expenseForm, accommodation_month_year: e.target.value})}
                  />
                </div>
              </div>
            )}

            {/* Description for Other/Manual */}
            {(expenseForm.expense_subtype === 'Other' || expenseForm.expense_type === 'miscellaneous') && (
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                  placeholder="Describe the expense"
                  rows={2}
                />
              </div>
            )}

            {/* Amount and Date */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount *</Label>
                <Input
                  type="number"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Date of Expense</Label>
                <Input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({...expenseForm, expense_date: e.target.value})}
                />
              </div>
            </div>

            {/* Paid To */}
            <div className="space-y-2">
              <Label>Paid To</Label>
              <Input
                value={expenseForm.paid_to}
                onChange={(e) => setExpenseForm({...expenseForm, paid_to: e.target.value})}
                placeholder="Company name, vendor, school, etc."
              />
            </div>

            {/* Payment Method */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select
                  value={expenseForm.payment_method}
                  onValueChange={(value) => setExpenseForm({...expenseForm, payment_method: value, payment_source: ''})}
                >
                  <SelectTrigger><SelectValue placeholder="Select method" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4" />
                        Bank Transfer
                      </div>
                    </SelectItem>
                    <SelectItem value="card_payment">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Card Payment
                      </div>
                    </SelectItem>
                    <SelectItem value="e_wallet">
                      <div className="flex items-center gap-2">
                        <Wallet className="w-4 h-4" />
                        E-wallet
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {expenseForm.payment_method && (
                <div className="space-y-2">
                  <Label>Payment Source</Label>
                  <Select
                    value={expenseForm.payment_source}
                    onValueChange={(value) => setExpenseForm({...expenseForm, payment_source: value})}
                  >
                    <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                    <SelectContent>
                      {getPaymentSources().map(source => (
                        <SelectItem key={source} value={source}>{source}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* File Uploads (multi-file: PDFs + images) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Upload Invoice(s)</Label>
                <Input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => setExpenseForm({
                    ...expenseForm,
                    new_invoices: [...(expenseForm.new_invoices || []), ...Array.from(e.target.files || [])],
                  })}
                  data-testid="expense-invoice-input"
                />
                {/* Existing files (edit mode) with remove chips */}
                {(expenseForm.existing_invoice_files || []).map((f, i) => (
                  <div key={`inv-existing-${i}`} className="flex items-center justify-between gap-2 text-[11px] rounded border border-blue-100 bg-blue-50 px-2 py-1">
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-700 hover:underline truncate"
                      data-testid={`existing-invoice-link-${i}`}
                    >
                      <FileText className="w-3 h-3 shrink-0" />
                      <span className="truncate">{f.filename || `Invoice ${i + 1}`}</span>
                    </a>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 text-xs shrink-0"
                      onClick={() => setExpenseForm({
                        ...expenseForm,
                        existing_invoice_files: (expenseForm.existing_invoice_files || []).filter((_, j) => j !== i),
                      })}
                      title="Remove"
                      data-testid={`remove-existing-invoice-${i}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {/* Newly picked files (pending upload) */}
                {(expenseForm.new_invoices || []).map((f, i) => (
                  <div key={`inv-new-${i}`} className="flex items-center justify-between gap-2 text-[11px] rounded border border-emerald-100 bg-emerald-50 px-2 py-1">
                    <span className="inline-flex items-center gap-1 text-emerald-700 truncate">
                      <Plus className="w-3 h-3 shrink-0" />
                      <span className="truncate">{f.name}</span>
                    </span>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 text-xs shrink-0"
                      onClick={() => setExpenseForm({
                        ...expenseForm,
                        new_invoices: (expenseForm.new_invoices || []).filter((_, j) => j !== i),
                      })}
                      title="Remove"
                      data-testid={`remove-new-invoice-${i}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
              <div className="space-y-2">
                <Label>Upload Receipt(s)</Label>
                <Input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => setExpenseForm({
                    ...expenseForm,
                    new_receipts: [...(expenseForm.new_receipts || []), ...Array.from(e.target.files || [])],
                  })}
                  data-testid="expense-receipt-input"
                />
                {(expenseForm.existing_receipt_files || []).map((f, i) => (
                  <div key={`rec-existing-${i}`} className="flex items-center justify-between gap-2 text-[11px] rounded border border-emerald-100 bg-emerald-50 px-2 py-1">
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-emerald-700 hover:underline truncate"
                      data-testid={`existing-receipt-link-${i}`}
                    >
                      <Eye className="w-3 h-3 shrink-0" />
                      <span className="truncate">{f.filename || `Receipt ${i + 1}`}</span>
                    </a>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 text-xs shrink-0"
                      onClick={() => setExpenseForm({
                        ...expenseForm,
                        existing_receipt_files: (expenseForm.existing_receipt_files || []).filter((_, j) => j !== i),
                      })}
                      title="Remove"
                      data-testid={`remove-existing-receipt-${i}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {(expenseForm.new_receipts || []).map((f, i) => (
                  <div key={`rec-new-${i}`} className="flex items-center justify-between gap-2 text-[11px] rounded border border-emerald-100 bg-emerald-50 px-2 py-1">
                    <span className="inline-flex items-center gap-1 text-emerald-700 truncate">
                      <Plus className="w-3 h-3 shrink-0" />
                      <span className="truncate">{f.name}</span>
                    </span>
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-700 text-xs shrink-0"
                      onClick={() => setExpenseForm({
                        ...expenseForm,
                        new_receipts: (expenseForm.new_receipts || []).filter((_, j) => j !== i),
                      })}
                      title="Remove"
                      data-testid={`remove-new-receipt-${i}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddExpense} disabled={loading} className="gap-2" data-testid="save-expense-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingExpenseId ? <Edit className="w-4 h-4" /> : <Plus className="w-4 h-4" />)}
              {editingExpenseId ? 'Save Changes' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Expenses Dialog */}
      <ExpenseListDialog 
        open={expenseListDialogOpen}
        onOpenChange={setExpenseListDialogOpen}
        candidate={selectedCandidate}
        getAuthHeader={getAuthHeader}
        onExpenseDeleted={fetchCandidates}
        onExpenseEdit={(expense) => {
          setExpenseListDialogOpen(false);
          handleEditExpense(expense);
        }}
      />

      {/* View Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents - {selectedCandidate?.name}
            </DialogTitle>
            <DialogDescription>View and download candidate documents</DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Passport Copy */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-blue-500" />
                  Passport Copy
                </Label>
                {selectedCandidate?.passport_copy_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.passport_copy_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.passport_copy_url, 
                    `passport_copy_${selectedCandidate.name}${getFileExtension(selectedCandidate.passport_copy_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Passport Photo */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <Image className="w-4 h-4 text-green-500" />
                  Passport Size Photo
                </Label>
                {selectedCandidate?.passport_photo_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.passport_photo_url && (
                <>
                  <img 
                    src={selectedCandidate.passport_photo_url} 
                    alt="Passport Photo" 
                    className="w-20 h-24 object-cover rounded border"
                  />
                  <button 
                    onClick={() => downloadFile(
                      selectedCandidate.passport_photo_url, 
                      `passport_photo_${selectedCandidate.name}${getFileExtension(selectedCandidate.passport_photo_url)}`
                    )}
                    className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </>
              )}
            </div>

            {/* Home Country License */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-amber-500" />
                  Home Country License
                </Label>
                {selectedCandidate?.home_license_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.home_license_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.home_license_url, 
                    `home_license_${selectedCandidate.name}${getFileExtension(selectedCandidate.home_license_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Home Country ID Card */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-purple-500" />
                  Home Country ID Card
                </Label>
                {selectedCandidate?.home_id_card_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.home_id_card_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.home_id_card_url, 
                    `home_id_card_${selectedCandidate.name}${getFileExtension(selectedCandidate.home_id_card_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Employment Agreement */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-indigo-500" />
                  Employment Agreement
                </Label>
                {selectedCandidate?.employment_agreement_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.employment_agreement_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.employment_agreement_url, 
                    `employment_agreement_${selectedCandidate.name}${getFileExtension(selectedCandidate.employment_agreement_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Loan Agreement */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-red-500" />
                  Loan Agreement
                </Label>
                {selectedCandidate?.loan_agreement_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.loan_agreement_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.loan_agreement_url, 
                    `loan_agreement_${selectedCandidate.name}${getFileExtension(selectedCandidate.loan_agreement_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Receipt of Cash Paid */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-teal-500" />
                  Receipt of Cash Paid
                </Label>
                {selectedCandidate?.receipt_cash_paid_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.receipt_cash_paid_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.receipt_cash_paid_url, 
                    `receipt_cash_paid_${selectedCandidate.name}${getFileExtension(selectedCandidate.receipt_cash_paid_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Additional Documents Section - Always show all documents */}
            <div className="col-span-2 border-t pt-4 mt-2">
              <p className="text-sm font-semibold text-slate-700 mb-3">Additional Documents (Optional)</p>
            </div>
            
            {/* MB MOHRE (Unsigned) */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-amber-500" />
                  MB MOHRE
                </Label>
                {selectedCandidate?.mb_mohre_unsigned_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.mb_mohre_unsigned_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.mb_mohre_unsigned_url, 
                    `mb_mohre_unsigned_${selectedCandidate.name}${getFileExtension(selectedCandidate.mb_mohre_unsigned_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* ST MOHRE (Unsigned) */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-indigo-500" />
                  ST MOHRE
                </Label>
                {selectedCandidate?.st_mohre_unsigned_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.st_mohre_unsigned_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.st_mohre_unsigned_url, 
                    `st_mohre_unsigned_${selectedCandidate.name}${getFileExtension(selectedCandidate.st_mohre_unsigned_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* Signed MB MOHRE */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-orange-500" />
                  Signed MB MOHRE
                </Label>
                {selectedCandidate?.mb_mohre_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.mb_mohre_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.mb_mohre_url, 
                    `mb_mohre_signed_${selectedCandidate.name}${getFileExtension(selectedCandidate.mb_mohre_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* Signed ST MOHRE */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-cyan-500" />
                  Signed ST MOHRE
                </Label>
                {selectedCandidate?.st_mohre_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.st_mohre_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.st_mohre_url, 
                    `st_mohre_signed_${selectedCandidate.name}${getFileExtension(selectedCandidate.st_mohre_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* Entry Permit */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-lime-500" />
                  Entry Permit
                </Label>
                {selectedCandidate?.entry_permit_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.entry_permit_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.entry_permit_url, 
                    `entry_permit_${selectedCandidate.name}${getFileExtension(selectedCandidate.entry_permit_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* Medical Report */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-pink-500" />
                  Medical Report
                </Label>
                {selectedCandidate?.medical_report_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.medical_report_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.medical_report_url, 
                    `medical_report_${selectedCandidate.name}${getFileExtension(selectedCandidate.medical_report_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* Emirates ID */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-violet-500" />
                  Emirates ID
                </Label>
                {selectedCandidate?.emirates_id_doc_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.emirates_id_doc_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.emirates_id_doc_url, 
                    `emirates_id_${selectedCandidate.name}${getFileExtension(selectedCandidate.emirates_id_doc_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
            
            {/* Visa Document */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-emerald-500" />
                  Visa
                </Label>
                {selectedCandidate?.visa_doc_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.visa_doc_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.visa_doc_url, 
                    `visa_${selectedCandidate.name}${getFileExtension(selectedCandidate.visa_doc_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* UAE Driving License */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-blue-600" />
                  UAE Driving License
                </Label>
                {selectedCandidate?.uae_driving_license_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.uae_driving_license_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.uae_driving_license_url, 
                    `uae_driving_license_${selectedCandidate.name}${getFileExtension(selectedCandidate.uae_driving_license_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>

            {/* Operational Health Certificate (OHC) */}
            <div className="border rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-medium flex items-center gap-2">
                  <File className="w-4 h-4 text-emerald-600" />
                  Operational Health Certificate
                </Label>
                {selectedCandidate?.operational_health_certificate_url ? (
                  <Badge variant="outline" className="bg-green-50 text-green-700">Uploaded</Badge>
                ) : (
                  <Badge variant="outline" className="bg-slate-50 text-slate-500">Not uploaded</Badge>
                )}
              </div>
              {selectedCandidate?.operational_health_certificate_url && (
                <button 
                  onClick={() => downloadFile(
                    selectedCandidate.operational_health_certificate_url, 
                    `ohc_${selectedCandidate.name}${getFileExtension(selectedCandidate.operational_health_certificate_url)}`
                  )}
                  className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                  data-testid="download-ohc-btn"
                >
                  <Download className="w-4 h-4" />
                  Download / View
                </button>
              )}
            </div>
          </div>

          {/* Documents from Hiring Page */}
          {selectedCandidate?.hiring_documents?.length > 0 && (
            <div className="border-t pt-4 mt-2">
              <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-orange-500" />
                Documents from Hiring Page
              </p>
              <div className="grid grid-cols-2 gap-4">
                {selectedCandidate.hiring_documents.map((doc, idx) => (
                  <div key={idx} className="border rounded-lg p-4 space-y-3 bg-orange-50/30" data-testid={`hiring-doc-${doc.type}`}>
                    <div className="flex items-center justify-between">
                      <Label className="font-medium flex items-center gap-2">
                        <File className="w-4 h-4 text-orange-500" />
                        {doc.label}
                        {doc.generated && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-600">Generated</Badge>}
                      </Label>
                      <Badge variant="outline" className="bg-green-50 text-green-700">From Hiring</Badge>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          const url = doc.url.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${doc.url}` 
                            : doc.url;
                          window.open(url, '_blank');
                        }}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                        data-testid={`hiring-doc-view-${doc.type}`}
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>
                      <button
                        onClick={() => {
                          const url = doc.url.startsWith('/api') 
                            ? `${process.env.REACT_APP_BACKEND_URL}${doc.url}` 
                            : doc.url;
                          downloadFile(url, `${doc.label.replace(/\s+/g, '_')}_${selectedCandidate.name}${getFileExtension(url)}`);
                        }}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:underline cursor-pointer"
                        data-testid={`hiring-doc-download-${doc.type}`}
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Candidate Details Dialog - View All Information */}
      <Dialog open={candidateDetailsDialogOpen} onOpenChange={setCandidateDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Users className="w-6 h-6" />
              Candidate Details - {selectedCandidate?.name}
            </DialogTitle>
            <DialogDescription>Complete information for this candidate</DialogDescription>
          </DialogHeader>
          
          {selectedCandidate && (
            <div className="space-y-6 py-4">
              {/* Basic Information */}
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Name</p>
                    <p className="font-medium">{selectedCandidate.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Nationality</p>
                    <p className="font-medium">{selectedCandidate.nationality || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Age</p>
                    <p className="font-medium">{selectedCandidate.age || computeAge(selectedCandidate.date_of_birth) || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Status</p>
                    <Badge 
                      variant="outline"
                      className={
                        selectedCandidate.status === 'ready' 
                          ? 'bg-green-100 text-green-700' 
                          : selectedCandidate.status === 'on_hold'
                            ? 'bg-red-100 text-red-700'
                            : selectedCandidate.status === 'cyclist'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                      }
                    >
                      {selectedCandidate.status === 'ready' ? 'Ready' : selectedCandidate.status === 'on_hold' ? 'On Hold' : selectedCandidate.status === 'cyclist' ? 'Cyclist' : 'In Process'}
                    </Badge>
                  </div>
                </div>
                <div className="mt-3">
                  <p className="text-slate-500 text-sm">Home Address</p>
                  <p className="font-medium text-sm">{selectedCandidate.home_address || '-'}</p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">UAE Mobile</p>
                    <p className="font-medium">{selectedCandidate.uae_mobile || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Home Country Mobile</p>
                    <p className="font-medium">{selectedCandidate.home_country_mobile || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Email</p>
                    <p className="font-medium">{selectedCandidate.email || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Company & Payment */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company & Payment
                </h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Lessee Company</p>
                    <p className="font-medium">{selectedCandidate.lessee_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Advance</p>
                    <p className="font-medium text-green-700">
                      <CurrencyDisplay amount={
                        Math.max(
                          advancePayments.reduce((sum, p) => sum + (p.amount || 0), 0),
                          selectedCandidate.advance_payment || 0
                        )
                      } />
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-500">Total Expenses</p>
                    <p className="font-medium text-purple-700"><CurrencyDisplay amount={selectedCandidate.total_expenses || 0} /></p>
                  </div>
                </div>

                {/* Advance Payments Breakdown */}
                {advancePayments.length > 0 && (
                  <div className="mt-4 border-t border-green-200 pt-3">
                    <p className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                      <Banknote className="w-3 h-3" />
                      Advance Payments Breakdown ({advancePayments.length} records)
                    </p>
                    <div className="space-y-2 max-h-[150px] overflow-y-auto">
                      {advancePayments.map((payment, idx) => (
                        <div 
                          key={payment.id} 
                          className="flex items-center justify-between bg-white rounded p-2 border border-green-100"
                        >
                          <div className="flex items-center gap-3">
                            <div className="text-xs font-mono text-green-600 w-32">{payment.transaction_ref || '-'}</div>
                            <div className="text-xs text-slate-500 w-20">{formatDateDisplay(payment.payment_date)}</div>
                            <div className="font-semibold text-green-700"><CurrencyDisplay amount={payment.amount} /></div>
                            {payment.notes && (
                              <span className="text-xs text-slate-400 truncate max-w-[150px]" title={payment.notes}>
                                {payment.notes}
                              </span>
                            )}
                          </div>
                          {payment.proof_url ? (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="h-7 px-2 gap-1 text-blue-600 hover:text-blue-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(`${process.env.REACT_APP_BACKEND_URL}${payment.proof_url}`, '_blank');
                              }}
                            >
                              <Download className="w-3 h-3" />
                              <span className="text-xs">
                                {payment.proof_url.toLowerCase().endsWith('.pdf') ? 'PDF' : 'Image'}
                              </span>
                            </Button>
                          ) : (
                            <span className="text-xs text-slate-400">No proof</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Documents Info */}
              <div className="bg-amber-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Document Information
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Passport Number</p>
                    <p className="font-medium">{selectedCandidate.passport_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Home Country ID Card</p>
                    <p className="font-medium">{selectedCandidate.home_country_id_card || '-'}</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-sm mt-3">
                  <div>
                    <p className="text-slate-500">License Number</p>
                    <p className="font-medium">{selectedCandidate.home_country_license?.license_number || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">License Issue Date</p>
                    <p className="font-medium">{selectedCandidate.home_country_license?.issue_date || '-'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">License Expiry Date</p>
                    <p className="font-medium">{selectedCandidate.home_country_license?.expiry_date || '-'}</p>
                  </div>
                </div>
              </div>

              {/* References */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Home Country References
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="border border-purple-200 rounded-lg p-3 bg-white">
                    <p className="font-medium text-purple-700 mb-2">Reference 1</p>
                    <div className="text-sm space-y-1">
                      <p><span className="text-slate-500">Name:</span> {selectedCandidate.reference1?.name || '-'}</p>
                      <p><span className="text-slate-500">Mobile:</span> {selectedCandidate.reference1?.mobile || '-'}</p>
                      <p><span className="text-slate-500">Address:</span> {selectedCandidate.reference1?.address || '-'}</p>
                    </div>
                  </div>
                  <div className="border border-purple-200 rounded-lg p-3 bg-white">
                    <p className="font-medium text-purple-700 mb-2">Reference 2</p>
                    <div className="text-sm space-y-1">
                      <p><span className="text-slate-500">Name:</span> {selectedCandidate.reference2?.name || '-'}</p>
                      <p><span className="text-slate-500">Mobile:</span> {selectedCandidate.reference2?.mobile || '-'}</p>
                      <p><span className="text-slate-500">Address:</span> {selectedCandidate.reference2?.address || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Uploaded Documents Summary */}
              <div className="bg-slate-100 rounded-lg p-4">
                <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Uploaded Documents
                </h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedCandidate.passport_copy_url || selectedCandidate.document_flags?.passport_copy_url) && <Badge className="bg-blue-100 text-blue-700">Passport Copy</Badge>}
                  {(selectedCandidate.passport_photo_url || selectedCandidate.document_flags?.passport_photo_url) && <Badge className="bg-green-100 text-green-700">Passport Photo</Badge>}
                  {(selectedCandidate.home_license_url || selectedCandidate.document_flags?.home_license_url) && <Badge className="bg-amber-100 text-amber-700">Home License</Badge>}
                  {(selectedCandidate.home_id_card_url || selectedCandidate.document_flags?.home_id_card_url) && <Badge className="bg-purple-100 text-purple-700">Home ID Card</Badge>}
                  {(selectedCandidate.employment_agreement_url || selectedCandidate.document_flags?.employment_agreement_url) && <Badge className="bg-indigo-100 text-indigo-700">Employment Agreement</Badge>}
                  {(selectedCandidate.loan_agreement_url || selectedCandidate.document_flags?.loan_agreement_url) && <Badge className="bg-red-100 text-red-700">Loan Agreement</Badge>}
                  {(selectedCandidate.receipt_cash_paid_url || selectedCandidate.document_flags?.receipt_cash_paid_url) && <Badge className="bg-teal-100 text-teal-700">Cash Receipt</Badge>}
                  {(selectedCandidate.mb_mohre_url || selectedCandidate.document_flags?.mb_mohre_url) && <Badge className="bg-cyan-100 text-cyan-700">Signed MB MOHRE</Badge>}
                  {(selectedCandidate.st_mohre_url || selectedCandidate.document_flags?.st_mohre_url) && <Badge className="bg-pink-100 text-pink-700">Signed ST MOHRE</Badge>}
                  {(selectedCandidate.entry_permit_url || selectedCandidate.document_flags?.entry_permit_url) && <Badge className="bg-orange-100 text-orange-700">Entry Permit</Badge>}
                  {(selectedCandidate.medical_report_url || selectedCandidate.document_flags?.medical_report_url) && <Badge className="bg-lime-100 text-lime-700">Medical Report</Badge>}
                  {(selectedCandidate.emirates_id_doc_url || selectedCandidate.document_flags?.emirates_id_doc_url) && <Badge className="bg-violet-100 text-violet-700">Emirates ID</Badge>}
                  {(selectedCandidate.visa_doc_url || selectedCandidate.document_flags?.visa_doc_url) && <Badge className="bg-rose-100 text-rose-700">Visa Doc</Badge>}
                  {(selectedCandidate.uae_driving_license_url || selectedCandidate.document_flags?.uae_driving_license_url) && <Badge className="bg-sky-100 text-sky-700">UAE Driving License</Badge>}
                  {(selectedCandidate.operational_health_certificate_url || selectedCandidate.document_flags?.operational_health_certificate_url) && <Badge className="bg-emerald-100 text-emerald-700">OHC</Badge>}
                  {!selectedCandidate.has_documents && !selectedCandidate.passport_copy_url && !selectedCandidate.passport_photo_url && 
                   !selectedCandidate.home_license_url && !selectedCandidate.home_id_card_url &&
                   !selectedCandidate.employment_agreement_url && !selectedCandidate.loan_agreement_url &&
                   !selectedCandidate.receipt_cash_paid_url && (
                    <span className="text-slate-500 text-sm">No documents uploaded</span>
                  )}
                </div>
              </div>

              {/* Export Status */}
              {selectedCandidate.imported_to_rider && (
                <div className="bg-blue-100 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    Exported to Riders
                  </h4>
                  <p className="text-sm text-blue-700">
                    This candidate has been exported to the Riders system on {selectedCandidate.imported_at ? formatDateDisplay(selectedCandidate.imported_at) : 'N/A'}
                  </p>
                  <p className="text-sm text-blue-600 mt-2">
                    <strong>Note:</strong> All expenses have been synced to the Loan Account in Talent PMS. 
                    To add more expenses, go to <strong>Talent PMS → Employee Accounts → Loan Details</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setEditCandidateDialogOpen(true);
                setCandidateDetailsDialogOpen(false);
                if (selectedCandidate?.id) {
                  fetchAdvancePayments(selectedCandidate.id);
                }
              }}
              className="gap-2"
            >
              <Edit className="w-4 h-4" />
              Edit Candidate
            </Button>
            <Button variant="outline" onClick={() => setCandidateDetailsDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Candidate Dialog */}
      <EditCandidateDialog 
        open={editCandidateDialogOpen}
        onOpenChange={setEditCandidateDialogOpen}
        candidate={selectedCandidate}
        lessees={lessees}
        getAuthHeader={getAuthHeader}
        onSave={() => {
          fetchCandidates();
          setEditCandidateDialogOpen(false);
        }}
        // Advance Payment Props
        advancePayments={advancePayments}
        advancePaymentForm={advancePaymentForm}
        setAdvancePaymentForm={setAdvancePaymentForm}
        handleAddAdvancePayment={handleAddAdvancePayment}
        addingAdvance={addingAdvance}
        setSelectedAdvancePayment={setSelectedAdvancePayment}
        setAdvanceDetailDialogOpen={setAdvanceDetailDialogOpen}
        isMobile={isMobile}
        // Export to Rider Props
        onExportToRider={handleImportToRider}
      />

      {/* Add Driving School Dialog */}
      <Dialog open={addDrivingSchoolDialogOpen} onOpenChange={setAddDrivingSchoolDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5" />
              Add Driving School
            </DialogTitle>
            <DialogDescription>Add a new driving school to the system</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>School Name *</Label>
              <Input
                value={newSchoolForm.name}
                onChange={(e) => setNewSchoolForm({...newSchoolForm, name: e.target.value})}
                placeholder="Enter school name"
              />
            </div>
            <div className="space-y-2">
              <Label>City</Label>
              <Input
                value={newSchoolForm.city}
                onChange={(e) => setNewSchoolForm({...newSchoolForm, city: e.target.value})}
                placeholder="e.g., Dubai, Abu Dhabi"
              />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Textarea
                value={newSchoolForm.address}
                onChange={(e) => setNewSchoolForm({...newSchoolForm, address: e.target.value})}
                placeholder="Full address"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setAddDrivingSchoolDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddDrivingSchool} disabled={loading} className="gap-2">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              Add School
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Advance Payment Detail Dialog */}
      <Dialog open={advanceDetailDialogOpen} onOpenChange={setAdvanceDetailDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-md'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />
              Advance Payment Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAdvancePayment && (
            <div className="space-y-4">
              {/* Transaction ID */}
              {selectedAdvancePayment.transaction_ref && (
                <div className="p-2 bg-blue-50 rounded-lg text-center">
                  <div className="text-xs text-slate-500">Transaction ID</div>
                  <div className="text-sm font-mono font-bold text-blue-600">
                    {selectedAdvancePayment.transaction_ref}
                  </div>
                </div>
              )}
              
              {/* Amount */}
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <div className="text-sm text-slate-500">Amount</div>
                <div className="text-2xl font-bold text-green-600">
                  <CurrencyDisplay amount={selectedAdvancePayment.amount} />
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500">Payment Date</div>
                  <div className="font-medium">{formatDateDisplay(selectedAdvancePayment.payment_date)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Recorded On</div>
                  <div className="font-medium">{formatDateDisplay(selectedAdvancePayment.created_at?.split('T')[0])}</div>
                </div>
              </div>

              {/* Notes */}
              {selectedAdvancePayment.notes && (
                <div>
                  <div className="text-xs text-slate-500">Notes</div>
                  <div className="text-sm mt-1 p-2 bg-slate-50 rounded">{selectedAdvancePayment.notes}</div>
                </div>
              )}

              {/* Proof of Payment */}
              {selectedAdvancePayment.proof_url ? (
                <div className="space-y-2">
                  <div className="text-xs text-slate-500">Proof of Payment</div>
                  <div className="border rounded-lg p-3">
                    {selectedAdvancePayment.proof_url.toLowerCase().endsWith('.pdf') ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <File className="w-8 h-8 text-red-500" />
                          <div>
                            <div className="font-medium text-sm">{selectedAdvancePayment.proof_filename || 'Payment Proof.pdf'}</div>
                            <div className="text-xs text-slate-500">PDF Document</div>
                          </div>
                        </div>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${selectedAdvancePayment.proof_url}`, '_blank')}
                          className="gap-1"
                        >
                          <Download className="w-4 h-4" />
                          Download
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <img 
                          src={`${process.env.REACT_APP_BACKEND_URL}${selectedAdvancePayment.proof_url}`}
                          alt="Payment Proof"
                          className="w-full max-h-[200px] object-contain rounded"
                        />
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="w-full gap-1"
                          onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${selectedAdvancePayment.proof_url}`, '_blank')}
                        >
                          <Download className="w-4 h-4" />
                          Download Image
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-4 text-slate-400 text-sm bg-slate-50 rounded-lg">
                  No proof document attached
                </div>
              )}

              {/* Delete Button */}
              <Button 
                variant="outline" 
                className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
                onClick={() => handleDeleteAdvancePayment(selectedAdvancePayment.id)}
              >
                <Trash2 className="w-4 h-4" />
                Delete This Advance Payment
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Expense List Dialog Component
const ExpenseListDialog = ({ open, onOpenChange, candidate, getAuthHeader, onExpenseDeleted, onExpenseEdit }) => {
  const [expenses, setExpenses] = useState([]);
  const [advPayments, setAdvPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [documentPreview, setDocumentPreview] = useState({ open: false, url: '', title: '' });

  useEffect(() => {
    if (open && candidate) {
      fetchData();
    }
  }, [open, candidate]);

  const fetchData = async () => {
    if (!candidate) return;
    setLoading(true);
    try {
      const [expRes, advRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tap/expenses/${candidate.id}`, { headers: getAuthHeader() }),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/tap/advance-payments/${candidate.id}`, { headers: getAuthHeader() })
      ]);
      setExpenses(expRes.data.expenses || []);
      setAdvPayments(advRes.data.payments || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/tap/expenses/${expenseId}`, {
        headers: getAuthHeader()
      });
      toast.success('Expense deleted');
      fetchData();
      onExpenseDeleted();
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  const formatExpenseType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const parts = dateStr.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    return dateStr;
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    return url.startsWith('data:image/') || url.match(/\.(jpg|jpeg|png|gif|webp)(\?|$)/i);
  };

  const isPdfUrl = (url) => {
    if (!url) return false;
    return url.startsWith('data:application/pdf') || url.match(/\.pdf(\?|$)/i);
  };

  const handleDownload = (url, filename) => {
    try {
      if (url.startsWith('data:')) {
        const [header, base64Data] = url.split(',');
        if (!base64Data) return;
        const mimeMatch = header.match(/data:([^;]+)/);
        const mimeType = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const blob = new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      window.open(url, '_blank');
    }
  };

  const getExtFromUrl = (url) => {
    if (!url) return '.pdf';
    if (url.startsWith('data:')) {
      if (url.includes('image/jpeg') || url.includes('image/jpg')) return '.jpg';
      if (url.includes('image/png')) return '.png';
      if (url.includes('application/pdf')) return '.pdf';
    }
    if (url.startsWith('http')) {
      const match = url.match(/\.(jpg|jpeg|png|pdf)(\?|$)/i);
      if (match) return `.${match[1].toLowerCase()}`;
    }
    return '.pdf';
  };

  // Compute totals from actual advance payment records
  const advanceAmount = Math.max(
    advPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
    candidate?.advance_payment || 0
  );
  const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
  const accountBalance = advanceAmount - totalExpenses;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="expense-statement-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Receipt className="w-5 h-5" />
              Account Statement
            </DialogTitle>
          </DialogHeader>

          {/* Employee Details */}
          <div className="bg-slate-50 rounded-lg p-4 border" data-testid="employee-details">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wide">Name</p>
                <p className="font-semibold text-slate-800">{candidate?.name || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wide">Employee ID</p>
                <p className="font-semibold text-slate-800">{candidate?.employee_unique_id || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wide">Lessee</p>
                <p className="font-semibold text-slate-800">{candidate?.lessee_name || '-'}</p>
              </div>
              <div>
                <p className="text-slate-500 text-xs uppercase tracking-wide">Status</p>
                <Badge variant="outline" className="mt-0.5">{candidate?.status || '-'}</Badge>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : (
            <>
              {/* Account Statement Table */}
              <div className="rounded-lg border overflow-hidden" data-testid="account-statement-table">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-100">
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-600 w-[12%]">Date</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-600 w-[33%]">Description</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-600 text-right w-[12%]">Credit</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-600 text-right w-[12%]">Debit</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-600 w-[19%]">Payment Method</TableHead>
                      <TableHead className="text-xs font-semibold uppercase tracking-wide text-slate-600 text-center w-[12%]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Advance Payment Rows */}
                    {advPayments.length > 0 ? (
                      advPayments.map((payment, idx) => (
                        <TableRow key={`adv-${payment.id}`} className="bg-green-50/50 border-b" data-testid={`advance-row-${idx}`}>
                          <TableCell className="text-sm text-slate-600">{formatDate(payment.payment_date)}</TableCell>
                          <TableCell>
                            <p className="text-sm font-medium text-slate-800">Advance Payment Received</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              {payment.transaction_ref && (
                                <span className="text-xs font-mono text-green-600">{payment.transaction_ref}</span>
                              )}
                              {payment.notes && (
                                <span className="text-xs text-slate-400">{payment.notes}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-sm font-semibold text-green-700">
                              <CurrencyDisplay amount={payment.amount || 0} />
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm text-slate-400">-</TableCell>
                          <TableCell className="text-xs text-slate-600">
                            {payment.transaction_ref
                              ? <span className="italic">Advance</span>
                              : <span className="text-slate-400">-</span>}
                          </TableCell>
                          <TableCell className="text-center">
                            {payment.proof_url ? (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const url = payment.proof_url.startsWith('http') 
                                    ? payment.proof_url 
                                    : `${process.env.REACT_APP_BACKEND_URL}${payment.proof_url}`;
                                  setDocumentPreview({ open: true, url, title: 'Payment Proof' });
                                }}
                              >
                                <Eye className="w-3.5 h-3.5" />
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : advanceAmount > 0 ? (
                      <TableRow className="bg-green-50/50 border-b" data-testid="advance-row">
                        <TableCell className="text-sm text-slate-600">-</TableCell>
                        <TableCell>
                          <p className="text-sm font-medium text-slate-800">Advance Received</p>
                          <p className="text-xs text-slate-500">Employee contribution</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="text-sm font-semibold text-green-700">
                            <CurrencyDisplay amount={advanceAmount} />
                          </span>
                        </TableCell>
                        <TableCell className="text-right text-sm text-slate-400">-</TableCell>
                        <TableCell className="text-xs text-slate-600"><span className="italic">Advance</span></TableCell>
                        <TableCell></TableCell>
                      </TableRow>
                    ) : null}

                    {/* Expense Rows */}
                    {expenses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-6 text-slate-500">
                          No expenses recorded yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      expenses.map((expense, idx) => (
                        <TableRow 
                          key={expense.id} 
                          className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                          data-testid={`expense-item-${idx}`}
                        >
                          <TableCell className="text-sm text-slate-600">
                            {formatDate(expense.expense_date)}
                          </TableCell>
                          <TableCell>
                            <p className="text-sm font-medium text-slate-800">{formatExpenseType(expense.expense_type)}</p>
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {expense.expense_subtype && (
                                <span className="text-xs text-slate-500">{expense.expense_subtype}</span>
                              )}
                              {expense.paid_to && (
                                <span className="text-xs text-slate-400">- Paid to: {expense.paid_to}</span>
                              )}
                            </div>
                            {expense.description && (
                              <p className="text-xs text-slate-400 mt-0.5">{expense.description}</p>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-sm text-slate-400">-</TableCell>
                          <TableCell className="text-right">
                            <span className="text-sm font-semibold text-red-600">
                              <CurrencyDisplay amount={expense.amount || 0} />
                            </span>
                          </TableCell>
                          <TableCell className="text-xs">
                            {(expense.payment_source || expense.payment_method) ? (
                              <div className="flex flex-col leading-tight" data-testid={`expense-payment-method-${idx}`}>
                                {expense.payment_source && (
                                  <span className="text-slate-700 font-medium break-words">{expense.payment_source}</span>
                                )}
                                {expense.payment_method && (
                                  <span className="text-[10px] uppercase tracking-wide text-slate-400">
                                    {expense.payment_method.replace(/_/g, ' ')}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {(expense.has_invoice_url || expense.invoice_url) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50 relative"
                                  title={`View / download invoice${(expense.invoice_files_count || (expense.invoice_files || []).length) > 1 ? `s (${expense.invoice_files_count || expense.invoice_files.length} files)` : ''}`}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    let url = expense.invoice_url;
                                    // Lazy-fetch the base64 payload when
                                    // it wasn't shipped with the row.
                                    if (!url) {
                                      try {
                                        const res = await axios.get(
                                          `${process.env.REACT_APP_BACKEND_URL}/api/tap/expenses/single/${expense.id}`,
                                          { headers: getAuthHeader() }
                                        );
                                        url = res.data?.invoice_url || '';
                                      } catch (_) { /* handled below */ }
                                    }
                                    if (url) setDocumentPreview({ open: true, url, title: 'Invoice' });
                                    else toast.error('Invoice file unavailable');
                                  }}
                                  data-testid={`expense-view-invoice-${expense.id}`}
                                >
                                  <FileText className="w-3.5 h-3.5" />
                                  {(expense.invoice_files_count || (expense.invoice_files || []).length) > 1 && (
                                    <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
                                      {expense.invoice_files_count || expense.invoice_files.length}
                                    </span>
                                  )}
                                </Button>
                              )}
                              {(expense.has_receipt_url || expense.receipt_url) && (
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50 relative"
                                  title={`View / download receipt${(expense.receipt_files_count || (expense.receipt_files || []).length) > 1 ? `s (${expense.receipt_files_count || expense.receipt_files.length} files)` : ''}`}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    let url = expense.receipt_url;
                                    if (!url) {
                                      try {
                                        const res = await axios.get(
                                          `${process.env.REACT_APP_BACKEND_URL}/api/tap/expenses/single/${expense.id}`,
                                          { headers: getAuthHeader() }
                                        );
                                        url = res.data?.receipt_url || '';
                                      } catch (_) { /* handled below */ }
                                    }
                                    if (url) setDocumentPreview({ open: true, url, title: 'Receipt' });
                                    else toast.error('Receipt file unavailable');
                                  }}
                                  data-testid={`expense-view-receipt-${expense.id}`}
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  {(expense.receipt_files_count || (expense.receipt_files || []).length) > 1 && (
                                    <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[9px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center leading-none">
                                      {expense.receipt_files_count || expense.receipt_files.length}
                                    </span>
                                  )}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 w-7 p-0 text-amber-600 hover:bg-amber-50"
                                title="Edit expense"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onExpenseEdit && onExpenseEdit(expense);
                                }}
                                data-testid={`expense-edit-${expense.id}`}
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                                title="Delete expense"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(expense.id);
                                }}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Account Summary */}
              <div className="rounded-lg border overflow-hidden" data-testid="account-summary">
                <div className="bg-slate-50 px-4 py-3 border-b">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Account Summary</p>
                </div>
                <div className="divide-y">
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-slate-600">Employee Contribution (Advance)</span>
                    <span className="text-sm font-semibold text-green-700"><CurrencyDisplay amount={advanceAmount} /></span>
                  </div>
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-sm text-slate-600">Paid by Company ({expenses.length} expense{expenses.length !== 1 ? 's' : ''})</span>
                    <span className="text-sm font-semibold text-red-600"><CurrencyDisplay amount={totalExpenses} /></span>
                  </div>
                  <div className={`flex items-center justify-between px-4 py-4 ${accountBalance >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                    <span className="text-sm font-bold text-slate-800">Account Balance</span>
                    <span className={`text-lg font-bold ${accountBalance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      <CurrencyDisplay amount={accountBalance} />
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Dialog */}
      <Dialog open={documentPreview.open} onOpenChange={(open) => setDocumentPreview({ ...documentPreview, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>{documentPreview.title}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center">
            {documentPreview.url && (
              <>
                {isImageUrl(documentPreview.url) ? (
                  <img 
                    src={documentPreview.url} 
                    alt={documentPreview.title}
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                ) : isPdfUrl(documentPreview.url) ? (
                  <iframe
                    src={documentPreview.url}
                    className="w-full h-[70vh] border rounded"
                    title={documentPreview.title}
                  />
                ) : (
                  <div className="text-center py-8">
                    <FileText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500 mb-4">Preview not available for this file type</p>
                    <Button onClick={() => window.open(documentPreview.url, '_blank')}>
                      <ExternalLink className="w-4 h-4 mr-2" /> Open in New Tab
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => handleDownload(documentPreview.url, `${documentPreview.title.toLowerCase()}_document${getExtFromUrl(documentPreview.url)}`)}>
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
            <Button variant="outline" onClick={() => setDocumentPreview({ ...documentPreview, open: false })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Edit Candidate Dialog Component
const EditCandidateDialog = ({ 
  open, onOpenChange, candidate, lessees, getAuthHeader, onSave,
  // Advance Payment Props
  advancePayments = [],
  advancePaymentForm = {},
  setAdvancePaymentForm = () => {},
  handleAddAdvancePayment = () => {},
  addingAdvance = false,
  setSelectedAdvancePayment = () => {},
  setAdvanceDetailDialogOpen = () => {},
  isMobile = false,
  // Export to Rider Props
  onExportToRider = () => {}
}) => {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [documentUploads, setDocumentUploads] = useState({});
  const [passportValidation, setPassportValidation] = useState({ checking: false, error: null, valid: true });
  const [eidValidation, setEidValidation] = useState({ checking: false, error: null, valid: true });
  const passportCheckTimeout = useRef(null);
  const eidCheckTimeout = useRef(null);
  
  const API = process.env.REACT_APP_BACKEND_URL ? process.env.REACT_APP_BACKEND_URL + '/api' : '/api';

  // Check if all UAE license fields are filled for "Ready" status export
  // Cyclists are allowed to export without UAE license fields (will be flagged as pending license)
  const isReadyForExport = () => {
    if (form.status !== 'ready' && form.status !== 'cyclist') return false;
    if (candidate?.imported_to_rider) return false; // Already exported

    // Cyclists bypass UAE license validation but still require City + full Contact Information
    if (form.status === 'cyclist') {
      return !!form.city && !!form.uae_mobile && !!form.home_country_mobile && !!form.email;
    }

    const hasLicenseNumber = !!form.uae_driving_license_no;
    const hasCity = !!form.city;
    const hasIssueDate = !!form.uae_driving_license_issue_date;
    const hasExpiryDate = !!form.uae_driving_license_expiry_date;
    const hasLicenseDoc = !!(candidate?.uae_driving_license_url || 
                            candidate?.document_flags?.uae_driving_license_url || 
                            documentUploads.uae_driving_license);
    const hasContact = !!form.uae_mobile && !!form.home_country_mobile && !!form.email;

    return hasLicenseNumber && hasCity && hasIssueDate && hasExpiryDate && hasLicenseDoc && hasContact;
  };

  // Handle export to rider
  // If form has unsaved changes (especially status differing from saved
  // candidate.status), persist them first so the backend sees the correct
  // status before the import-to-rider call.
  const handleExport = async () => {
    if (!candidate?.id) return;

    setExporting(true);
    try {
      const savedStatus = candidate?.status;
      const needsSave = form.status !== savedStatus;

      if (needsSave) {
        const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
        const formData = new FormData();
        Object.keys(form).forEach(key => {
          formData.append(key, form[key] || '');
        });
        if (documentUploads.uae_driving_license) {
          formData.append('uae_driving_license', documentUploads.uae_driving_license);
        }
        await axios.put(`${API_BASE}/tap/candidates/${candidate.id}`, formData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });

        // Upload any other queued documents alongside the save
        const otherDocs = { ...documentUploads };
        delete otherDocs.uae_driving_license;
        if (Object.keys(otherDocs).length > 0) {
          const docFormData = new FormData();
          Object.entries(otherDocs).forEach(([key, file]) => {
            if (file) docFormData.append(key, file);
          });
          await axios.put(`${API_BASE}/tap/candidates/${candidate.id}/documents`, docFormData, {
            headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
          });
        }
      }

      await onExportToRider(candidate.id);
      onOpenChange(false);
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to export candidate'));
    } finally {
      setExporting(false);
    }
  };
  
  // Check passport duplicate
  const checkPassportDuplicate = async (passportNumber) => {
    if (!passportNumber || passportNumber.trim().length < 3) {
      setPassportValidation({ checking: false, error: null, valid: true });
      return;
    }
    
    // Don't check if passport hasn't changed
    const currentPassport = candidate?.passport_number || candidate?.passport_no || '';
    if (passportNumber.trim().toUpperCase() === currentPassport.toUpperCase()) {
      setPassportValidation({ checking: false, error: null, valid: true });
      return;
    }
    
    setPassportValidation({ checking: true, error: null, valid: true });
    
    try {
      const url = `${API}/tap/check-passport/${encodeURIComponent(passportNumber)}?exclude_id=${candidate?.id}`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      
      if (response.data.exists) {
        setPassportValidation({ 
          checking: false, 
          error: response.data.message,
          valid: false
        });
      } else {
        setPassportValidation({ checking: false, error: null, valid: true });
      }
    } catch (error) {
      console.error('Error checking passport:', error);
      setPassportValidation({ checking: false, error: null, valid: true });
    }
  };
  
  // Debounced check
  const handlePassportChange = (value) => {
    setForm({...form, passport_number: value});
    if (passportCheckTimeout.current) clearTimeout(passportCheckTimeout.current);
    passportCheckTimeout.current = setTimeout(() => checkPassportDuplicate(value), 500);
  };

  // Emirates ID duplicate check (mirrors passport pattern)
  const checkEidDuplicate = async (eidValue) => {
    const digits = (eidValue || '').replace(/\D/g, '');
    if (!digits) {
      setEidValidation({ checking: false, error: null, valid: true });
      return;
    }
    const currentDigits = ((candidate?.emirates_id) || '').replace(/\D/g, '');
    if (digits === currentDigits) {
      setEidValidation({ checking: false, error: null, valid: true });
      return;
    }
    setEidValidation({ checking: true, error: null, valid: true });
    try {
      const url = `${API}/tap/check-emirates-id/${encodeURIComponent(eidValue)}?exclude_id=${candidate?.id || ''}`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      if (response.data.exists) {
        setEidValidation({ checking: false, error: response.data.message, valid: false });
      } else {
        setEidValidation({ checking: false, error: null, valid: true });
      }
    } catch (e) {
      setEidValidation({ checking: false, error: null, valid: true });
    }
  };

  const handleEidChange = (value) => {
    setForm({ ...form, emirates_id: value });
    if (eidCheckTimeout.current) clearTimeout(eidCheckTimeout.current);
    eidCheckTimeout.current = setTimeout(() => checkEidDuplicate(value), 500);
  };

  useEffect(() => {
    if (candidate && open) {
      setPassportValidation({ checking: false, error: null, valid: true });
      setEidValidation({ checking: false, error: null, valid: true });
      setForm({
        name: candidate.name || '',
        nationality: candidate.nationality || '',
        date_of_birth: candidate.date_of_birth || '',
        home_address: candidate.home_address || '',
        email: candidate.email || '',
        city: candidate.city || '',
        uae_mobile: candidate.uae_mobile || '',
        home_country_mobile: candidate.home_country_mobile || '',
        ref1_name: candidate.reference1?.name || '',
        ref1_mobile: candidate.reference1?.mobile || '',
        ref1_address: candidate.reference1?.address || '',
        ref2_name: candidate.reference2?.name || '',
        ref2_mobile: candidate.reference2?.mobile || '',
        ref2_address: candidate.reference2?.address || '',
        license_number: candidate.home_country_license?.license_number || '',
        license_issue_date: candidate.home_country_license?.issue_date || '',
        license_expiry_date: candidate.home_country_license?.expiry_date || '',
        home_country_id_card: candidate.home_country_id_card || '',
        passport_number: candidate.passport_number || '',
        emirates_id: candidate.emirates_id || '',
        lessee_id: candidate.lessee_id || '',
        lessee_name: candidate.lessee_name || '',
        advance_payment: candidate.advance_payment || '',
        status: candidate.status || 'in_process',
        // UAE Driving License fields
        uae_driving_license_no: candidate.uae_driving_license_no || '',
        uae_driving_license_issue_date: candidate.uae_driving_license_issue_date || '',
        uae_driving_license_expiry_date: candidate.uae_driving_license_expiry_date || ''
      });
      setDocumentUploads({});
    }
  }, [candidate, open]);

  const handleSubmit = async () => {
    // Check passport validation before submit
    if (!passportValidation.valid) {
      toast.error('Please fix the passport number issue before saving');
      return;
    }
    if (!eidValidation.valid) {
      toast.error('Please fix the Emirates ID issue before saving');
      return;
    }
    
    // UAE driving license is REQUIRED only when status = Ready.
    // Cyclist is a transitional status and can be saved (and exported as
    // a rider) without a license. Promotion from Cyclist → Ready will
    // then re-trigger this validation.
    if (form.status === 'ready') {
      const missingFields = [];
      if (!form.uae_driving_license_no) missingFields.push('UAE Driving License No.');
      if (!form.city) missingFields.push('City');
      if (!form.uae_driving_license_issue_date) missingFields.push('License Issue Date');
      if (!form.uae_driving_license_expiry_date) missingFields.push('License Expiry Date');
      if (!candidate?.uae_driving_license_url && !candidate?.document_flags?.uae_driving_license_url && !documentUploads.uae_driving_license) {
        missingFields.push('UAE Driving License (upload)');
      }
      // Contact Information also required for Ready
      if (!form.uae_mobile) missingFields.push('UAE Mobile');
      if (!form.home_country_mobile) missingFields.push('Home Country Mobile');
      if (!form.email) missingFields.push('Email Address');

      if (missingFields.length > 0) {
        toast.error(`Cannot mark candidate as Ready — missing: ${missingFields.join(', ')}`);
        return;
      }
    }

    // Cyclist requires City + full Contact Information (UAE Mobile, Home Country Mobile, Email)
    if (form.status === 'cyclist') {
      const missingCyclistFields = [];
      if (!form.city) missingCyclistFields.push('City');
      if (!form.uae_mobile) missingCyclistFields.push('UAE Mobile');
      if (!form.home_country_mobile) missingCyclistFields.push('Home Country Mobile');
      if (!form.email) missingCyclistFields.push('Email Address');

      if (missingCyclistFields.length > 0) {
        toast.error(`Cannot mark candidate as Cyclist — missing: ${missingCyclistFields.join(', ')}`);
        return;
      }
    }
    
    setLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL + '/api';
      
      // First update basic info including UAE driving license fields
      const formData = new FormData();
      Object.keys(form).forEach(key => {
        formData.append(key, form[key] || '');
      });
      
      // Add UAE driving license file if uploaded
      if (documentUploads.uae_driving_license) {
        formData.append('uae_driving_license', documentUploads.uae_driving_license);
      }
      
      await axios.put(`${API}/tap/candidates/${candidate.id}`, formData, {
        headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
      });

      // Upload new documents if any (excluding uae_driving_license which was already sent)
      const otherDocs = { ...documentUploads };
      delete otherDocs.uae_driving_license;
      
      if (Object.keys(otherDocs).length > 0) {
        const docFormData = new FormData();
        Object.entries(otherDocs).forEach(([key, file]) => {
          if (file) docFormData.append(key, file);
        });
        
        await axios.put(`${API}/tap/candidates/${candidate.id}/documents`, docFormData, {
          headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success('Candidate updated successfully');
      onSave();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Failed to update candidate'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Edit Candidate - {candidate?.name}
          </DialogTitle>
          <DialogDescription>Update candidate information and documents</DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Basic Information */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Users className="w-4 h-4" />
              Basic Information
            </h4>
            <div className="grid grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Name *</Label>
                <Input 
                  value={form.name || ''} 
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Nationality</Label>
                <Input 
                  value={form.nationality || ''} 
                  onChange={(e) => setForm({...form, nationality: e.target.value})}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Date of Birth</Label>
                <Input 
                  type="date"
                  value={form.date_of_birth || ''} 
                  onChange={(e) => setForm({...form, date_of_birth: e.target.value})}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Status</Label>
                <Select value={form.status || 'in_process'} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_process">In Process</SelectItem>
                    <SelectItem value="ready">Ready</SelectItem>
                    <SelectItem value="on_hold">On Hold</SelectItem>
                    <SelectItem value="cyclist">Cyclist</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Home Address</Label>
              <Input 
                value={form.home_address || ''} 
                onChange={(e) => setForm({...form, home_address: e.target.value})}
                className="h-9"
              />
            </div>
          </div>

          {/* UAE Driving License Section — Required ONLY for Ready status.
              Cyclist is a transitional status: the candidate can be
              exported as a rider WITHOUT a license, but a safeguard at
              promotion-to-Ready time forces the license to be captured. */}
          {form.status === 'ready' && (
            <div className="space-y-3 border-2 border-orange-200 bg-orange-50 rounded-lg p-4">
              <h4 className="font-semibold text-orange-800 flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                UAE Driving License (Required for Ready Status)
              </h4>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    UAE Driving License No. <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    value={form.uae_driving_license_no || ''} 
                    onChange={(e) => setForm({...form, uae_driving_license_no: e.target.value})}
                    placeholder="DXB-XXXXXX"
                    className={`h-9 ${!form.uae_driving_license_no ? 'border-orange-400' : ''}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <Select value={form.city || ''} onValueChange={(v) => setForm({...form, city: v})}>
                    <SelectTrigger className={`h-9 ${!form.city ? 'border-orange-400' : ''}`}>
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dubai">Dubai</SelectItem>
                      <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                      <SelectItem value="Sharjah">Sharjah</SelectItem>
                      <SelectItem value="Ajman">Ajman</SelectItem>
                      <SelectItem value="RAK">Ras Al Khaimah</SelectItem>
                      <SelectItem value="Fujairah">Fujairah</SelectItem>
                      <SelectItem value="UAQ">Umm Al Quwain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    Issue Date <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    type="date"
                    value={form.uae_driving_license_issue_date || ''} 
                    onChange={(e) => setForm({...form, uae_driving_license_issue_date: e.target.value})}
                    className={`h-9 ${!form.uae_driving_license_issue_date ? 'border-orange-400' : ''}`}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">
                    Expiry Date <span className="text-red-500">*</span>
                  </Label>
                  <Input 
                    type="date"
                    value={form.uae_driving_license_expiry_date || ''} 
                    onChange={(e) => setForm({...form, uae_driving_license_expiry_date: e.target.value})}
                    className={`h-9 ${!form.uae_driving_license_expiry_date ? 'border-orange-400' : ''}`}
                  />
                </div>
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs flex items-center gap-2">
                    Upload UAE Driving License <span className="text-red-500">*</span>
                    {(candidate?.uae_driving_license_url || candidate?.document_flags?.uae_driving_license_url) && (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                    )}
                  </Label>
                  <Input 
                    type="file" 
                    accept="image/*,.pdf"
                    onChange={(e) => setDocumentUploads({...documentUploads, uae_driving_license: e.target.files[0]})}
                    className={`h-9 ${!candidate?.uae_driving_license_url && !candidate?.document_flags?.uae_driving_license_url && !documentUploads.uae_driving_license ? 'border-orange-400' : ''}`}
                  />
                  {documentUploads.uae_driving_license && <span className="text-xs text-blue-600">New: {documentUploads.uae_driving_license.name}</span>}
                </div>
              </div>
              {/* Validation message */}
              {(!form.uae_driving_license_no || !form.city || !form.uae_driving_license_issue_date || 
                !form.uae_driving_license_expiry_date || 
                (!candidate?.uae_driving_license_url && !candidate?.document_flags?.uae_driving_license_url && !documentUploads.uae_driving_license)) && (
                <p className="text-xs text-orange-700 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  All UAE driving license fields are required to export candidate to Riders page
                </p>
              )}
            </div>
          )}

          {/* For Cyclist status, City is the only mandatory field.
              UAE driving license is collected later when promoted to Ready. */}
          {form.status === 'cyclist' && (
            <div className="space-y-3 border-2 border-amber-200 bg-amber-50 rounded-lg p-4">
              <h4 className="font-semibold text-amber-800 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Cyclist Onboarding — City Required
              </h4>
              <p className="text-xs text-amber-700">
                Cyclists can be exported as riders without a UAE driving license.
                Only City is required. The rider will be flagged as
                "license pending" until promoted to Ready.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">
                    City <span className="text-red-500">*</span>
                  </Label>
                  <Select value={form.city || ''} onValueChange={(v) => setForm({...form, city: v})}>
                    <SelectTrigger
                      className={`h-9 ${!form.city ? 'border-amber-400' : ''}`}
                      data-testid="cyclist-city-select-trigger"
                    >
                      <SelectValue placeholder="Select city" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Dubai">Dubai</SelectItem>
                      <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                      <SelectItem value="Sharjah">Sharjah</SelectItem>
                      <SelectItem value="Ajman">Ajman</SelectItem>
                      <SelectItem value="RAK">Ras Al Khaimah</SelectItem>
                      <SelectItem value="Fujairah">Fujairah</SelectItem>
                      <SelectItem value="UAQ">Umm Al Quwain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {!form.city && (
                <p className="text-xs text-amber-700 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  City is required to export this candidate as a Cyclist
                </p>
              )}
            </div>
          )}

          {/* Contact Information — All 3 fields required for Cyclist or Ready status */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Phone className="w-4 h-4" />
              Contact Information
              {(form.status === 'cyclist' || form.status === 'ready') && (
                <span className="text-xs font-normal text-amber-700">
                  (All fields required for {form.status === 'cyclist' ? 'Cyclist' : 'Ready'})
                </span>
              )}
            </h4>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">
                  UAE Mobile {(form.status === 'cyclist' || form.status === 'ready') && <span className="text-red-500">*</span>}
                </Label>
                <Input 
                  value={form.uae_mobile || ''} 
                  onChange={(e) => setForm({...form, uae_mobile: e.target.value})}
                  className={`h-9 ${(form.status === 'cyclist' || form.status === 'ready') && !form.uae_mobile ? 'border-amber-400' : ''}`}
                  data-testid="edit-uae-mobile-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  Home Country Mobile {(form.status === 'cyclist' || form.status === 'ready') && <span className="text-red-500">*</span>}
                </Label>
                <Input 
                  value={form.home_country_mobile || ''} 
                  onChange={(e) => setForm({...form, home_country_mobile: e.target.value})}
                  className={`h-9 ${(form.status === 'cyclist' || form.status === 'ready') && !form.home_country_mobile ? 'border-amber-400' : ''}`}
                  data-testid="edit-home-mobile-input"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">
                  Email Address {(form.status === 'cyclist' || form.status === 'ready') && <span className="text-red-500">*</span>}
                </Label>
                <Input 
                  type="email"
                  value={form.email || ''} 
                  onChange={(e) => setForm({...form, email: e.target.value})}
                  className={`h-9 ${(form.status === 'cyclist' || form.status === 'ready') && !form.email ? 'border-amber-400' : ''}`}
                  data-testid="edit-email-input"
                />
              </div>
            </div>
          </div>

          {/* Company & Payment */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Building2 className="w-4 h-4" />
              Company & Payment
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Lessee Company</Label>
                <Select 
                  value={form.lessee_id || ''} 
                  onValueChange={(v) => {
                    const lessee = lessees.find(l => l.id === v);
                    setForm({...form, lessee_id: v, lessee_name: lessee?.company_name || ''});
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Select lessee" />
                  </SelectTrigger>
                  <SelectContent>
                    {lessees.map(l => (
                      <SelectItem key={l.id} value={l.id}>{l.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Total Advance Payment (Editable)</Label>
                <Input 
                  type="number"
                  placeholder="0.00"
                  value={form.advance_payment || ''}
                  onChange={(e) => setForm({...form, advance_payment: parseFloat(e.target.value) || 0})}
                  className="h-9 font-semibold text-green-600 border-green-300 focus:border-green-500"
                />
                <p className="text-xs text-slate-500">Edit this value to correct the advance payment amount</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Loan Contract Amount (Editable)</Label>
                <Input 
                  type="number"
                  placeholder="0.00"
                  value={form.loan_contract_value || ''}
                  onChange={(e) => setForm({...form, loan_contract_value: parseFloat(e.target.value) || 0})}
                  className="h-9 font-semibold text-indigo-600 border-indigo-300 focus:border-indigo-500"
                />
                <p className="text-xs text-slate-500">Agreed loan contract amount for this candidate</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">No. of Installments (Editable)</Label>
                <Input 
                  type="number"
                  min="1"
                  placeholder="e.g. 12"
                  value={form.no_of_installments || ''}
                  onChange={(e) => setForm({...form, no_of_installments: parseInt(e.target.value) || 0})}
                  className="h-9 font-semibold text-indigo-600 border-indigo-300 focus:border-indigo-500"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Installment Amount (AED)</Label>
                <div className="flex items-center h-9 px-3 border rounded-md bg-slate-50 text-sm font-semibold text-blue-700">
                  {parseFloat(form.loan_contract_value) > 0 && parseInt(form.no_of_installments) > 0
                    ? (parseFloat(form.loan_contract_value) / parseInt(form.no_of_installments)).toFixed(2)
                    : '0.00'}
                </div>
              </div>
            </div>
          </div>

          {/* Advance Payments Section */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Banknote className="w-4 h-4" />
              Advance Payments Log
              <Badge variant="secondary" className="ml-2">{advancePayments.length} records</Badge>
            </h4>
            
            {/* Add New Advance Payment Form */}
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-3">
              <div className="text-xs font-medium text-green-700">Add New Advance Payment</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs">Amount (AED)</Label>
                  <Input 
                    type="number"
                    placeholder="0.00"
                    value={advancePaymentForm.amount} 
                    onChange={(e) => setAdvancePaymentForm({...advancePaymentForm, amount: e.target.value})}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Payment Date</Label>
                  <Input 
                    type="date"
                    value={advancePaymentForm.payment_date} 
                    onChange={(e) => setAdvancePaymentForm({...advancePaymentForm, payment_date: e.target.value})}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Proof (Image/PDF)</Label>
                  <Input 
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setAdvancePaymentForm({...advancePaymentForm, proof_file: e.target.files?.[0] || null})}
                    className="h-8 text-xs"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="Notes (optional)"
                  value={advancePaymentForm.notes} 
                  onChange={(e) => setAdvancePaymentForm({...advancePaymentForm, notes: e.target.value})}
                  className="h-8 text-sm flex-1"
                />
                <Button 
                  type="button" 
                  size="sm" 
                  onClick={handleAddAdvancePayment}
                  disabled={addingAdvance}
                  className="gap-1"
                >
                  {addingAdvance ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Add
                </Button>
              </div>
            </div>

            {/* Advance Payments Log */}
            {advancePayments.length > 0 ? (
              <div className="border rounded-lg divide-y max-h-[150px] overflow-y-auto">
                {advancePayments.map((payment) => (
                  <div 
                    key={payment.id} 
                    className="p-2 hover:bg-blue-50 cursor-pointer flex items-center justify-between text-sm"
                    onClick={() => {
                      setSelectedAdvancePayment(payment);
                      setAdvanceDetailDialogOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="text-xs font-mono text-green-600">{payment.transaction_ref || '-'}</div>
                      <div className="text-xs text-slate-500">{formatDateDisplay(payment.payment_date)}</div>
                      <div className="font-semibold text-green-600"><CurrencyDisplay amount={payment.amount} /></div>
                      {payment.proof_url && (
                        <Badge variant="outline" className="text-xs gap-1">
                          {payment.proof_url.endsWith('.pdf') ? <File className="w-3 h-3" /> : <Image className="w-3 h-3" />}
                          Proof
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {payment.notes && <span className="text-xs text-slate-500 truncate max-w-[100px]">{payment.notes}</span>}
                      <Eye className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500 text-sm bg-slate-50 rounded-lg">
                No advance payments recorded
              </div>
            )}
          </div>

          {/* Document Information */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Document Information
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Passport Number</Label>
                <Input 
                  value={form.passport_number || ''} 
                  onChange={(e) => handlePassportChange(e.target.value)}
                  className={`h-9 ${!passportValidation.valid ? 'border-red-500' : ''}`}
                />
                {passportValidation.checking && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Checking...
                  </p>
                )}
                {!passportValidation.valid && passportValidation.error && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {passportValidation.error}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Emirates ID No.</Label>
                <Input
                  data-testid="tap-edit-emirates-id-input"
                  value={form.emirates_id || ''}
                  onChange={(e) => handleEidChange(e.target.value)}
                  placeholder="784-YYYY-XXXXXXX-X"
                  className={`h-9 ${!eidValidation.valid ? 'border-red-500' : ''}`}
                />
                {eidValidation.checking && (
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Checking...
                  </p>
                )}
                {!eidValidation.valid && eidValidation.error && (
                  <p className="text-xs text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> {eidValidation.error}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Home Country ID Card</Label>
                <Input 
                  value={form.home_country_id_card || ''} 
                  onChange={(e) => setForm({...form, home_country_id_card: e.target.value})}
                  className="h-9"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">License Number</Label>
                <Input 
                  value={form.license_number || ''} 
                  onChange={(e) => setForm({...form, license_number: e.target.value})}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">License Issue Date</Label>
                <Input 
                  type="date"
                  value={form.license_issue_date || ''} 
                  onChange={(e) => setForm({...form, license_issue_date: e.target.value})}
                  className="h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">License Expiry Date</Label>
                <Input 
                  type="date"
                  value={form.license_expiry_date || ''} 
                  onChange={(e) => setForm({...form, license_expiry_date: e.target.value})}
                  className="h-9"
                />
              </div>
            </div>
          </div>

          {/* References */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Home Country References
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="border rounded-lg p-3 space-y-2">
                <p className="font-medium text-sm text-purple-700">Reference 1</p>
                <Input 
                  placeholder="Name" 
                  value={form.ref1_name || ''} 
                  onChange={(e) => setForm({...form, ref1_name: e.target.value})}
                  className="h-8 text-sm"
                />
                <Input 
                  placeholder="Mobile" 
                  value={form.ref1_mobile || ''} 
                  onChange={(e) => setForm({...form, ref1_mobile: e.target.value})}
                  className="h-8 text-sm"
                />
                <Input 
                  placeholder="Address" 
                  value={form.ref1_address || ''} 
                  onChange={(e) => setForm({...form, ref1_address: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>
              <div className="border rounded-lg p-3 space-y-2">
                <p className="font-medium text-sm text-purple-700">Reference 2</p>
                <Input 
                  placeholder="Name" 
                  value={form.ref2_name || ''} 
                  onChange={(e) => setForm({...form, ref2_name: e.target.value})}
                  className="h-8 text-sm"
                />
                <Input 
                  placeholder="Mobile" 
                  value={form.ref2_mobile || ''} 
                  onChange={(e) => setForm({...form, ref2_mobile: e.target.value})}
                  className="h-8 text-sm"
                />
                <Input 
                  placeholder="Address" 
                  value={form.ref2_address || ''} 
                  onChange={(e) => setForm({...form, ref2_address: e.target.value})}
                  className="h-8 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Upload New Documents */}
          <div className="space-y-3">
            <h4 className="font-semibold text-slate-700 flex items-center gap-2">
              <Upload className="w-4 h-4" />
              Upload / Replace Documents
            </h4>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Passport Copy
                  {(candidate?.passport_copy_url || candidate?.document_flags?.passport_copy_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, passport_copy: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.passport_copy && <span className="text-xs text-blue-600">New: {documentUploads.passport_copy.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Passport Photo
                  {(candidate?.passport_photo_url || candidate?.document_flags?.passport_photo_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => setDocumentUploads({...documentUploads, passport_photo: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.passport_photo && <span className="text-xs text-blue-600">New: {documentUploads.passport_photo.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Home License
                  {(candidate?.home_license_url || candidate?.document_flags?.home_license_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, home_license: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.home_license && <span className="text-xs text-blue-600">New: {documentUploads.home_license.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Home ID Card
                  {(candidate?.home_id_card_url || candidate?.document_flags?.home_id_card_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, home_id_card: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.home_id_card && <span className="text-xs text-blue-600">New: {documentUploads.home_id_card.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Employment Agreement
                  {(candidate?.employment_agreement_url || candidate?.document_flags?.employment_agreement_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, employment_agreement: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.employment_agreement && <span className="text-xs text-blue-600">New: {documentUploads.employment_agreement.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Loan Agreement
                  {(candidate?.loan_agreement_url || candidate?.document_flags?.loan_agreement_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, loan_agreement: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.loan_agreement && <span className="text-xs text-blue-600">New: {documentUploads.loan_agreement.name}</span>}
              </div>
              <div className="space-y-1 col-span-2">
                <Label className="text-xs flex items-center gap-2">
                  Receipt of Cash Paid
                  {(candidate?.receipt_cash_paid_url || candidate?.document_flags?.receipt_cash_paid_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, receipt_cash_paid: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.receipt_cash_paid && <span className="text-xs text-blue-600">New: {documentUploads.receipt_cash_paid.name}</span>}
              </div>
            </div>
            
            {/* Additional Optional Documents */}
            <p className="text-sm font-medium text-slate-600 mt-3">Additional Documents (Optional)</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  MB MOHRE
                  {(candidate?.mb_mohre_unsigned_url || candidate?.document_flags?.mb_mohre_unsigned_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, mb_mohre_unsigned: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.mb_mohre_unsigned && <span className="text-xs text-blue-600">New: {documentUploads.mb_mohre_unsigned.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  ST MOHRE
                  {(candidate?.st_mohre_unsigned_url || candidate?.document_flags?.st_mohre_unsigned_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, st_mohre_unsigned: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.st_mohre_unsigned && <span className="text-xs text-blue-600">New: {documentUploads.st_mohre_unsigned.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Signed MB MOHRE
                  {(candidate?.mb_mohre_url || candidate?.document_flags?.mb_mohre_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, mb_mohre: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.mb_mohre && <span className="text-xs text-blue-600">New: {documentUploads.mb_mohre.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Signed ST MOHRE
                  {(candidate?.st_mohre_url || candidate?.document_flags?.st_mohre_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, st_mohre: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.st_mohre && <span className="text-xs text-blue-600">New: {documentUploads.st_mohre.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Entry Permit
                  {(candidate?.entry_permit_url || candidate?.document_flags?.entry_permit_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, entry_permit: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.entry_permit && <span className="text-xs text-blue-600">New: {documentUploads.entry_permit.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Medical Report
                  {(candidate?.medical_report_url || candidate?.document_flags?.medical_report_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, medical_report: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.medical_report && <span className="text-xs text-blue-600">New: {documentUploads.medical_report.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Emirates ID
                  {(candidate?.emirates_id_doc_url || candidate?.document_flags?.emirates_id_doc_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, emirates_id_doc: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.emirates_id_doc && <span className="text-xs text-blue-600">New: {documentUploads.emirates_id_doc.name}</span>}
              </div>
              <div className="space-y-1">
                <Label className="text-xs flex items-center gap-2">
                  Visa
                  {(candidate?.visa_doc_url || candidate?.document_flags?.visa_doc_url) && (
                    <Badge variant="outline" className="text-xs bg-green-50 text-green-700">✓ Uploaded</Badge>
                  )}
                </Label>
                <Input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={(e) => setDocumentUploads({...documentUploads, visa_doc: e.target.files[0]})}
                  className="h-9 text-sm"
                />
                {documentUploads.visa_doc && <span className="text-xs text-blue-600">New: {documentUploads.visa_doc.name}</span>}
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 flex-wrap sm:flex-nowrap">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
          {/* Export to Rider button - visible for Ready (with all license fields) or Cyclist (license bypass) */}
          {(form.status === 'ready' || form.status === 'cyclist') && !candidate?.imported_to_rider && (
            <Button 
              onClick={handleExport} 
              disabled={exporting || !isReadyForExport()}
              className={`gap-2 ${isReadyForExport() ? (form.status === 'cyclist' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700') : 'bg-gray-400 cursor-not-allowed'}`}
              data-testid="export-to-rider-btn"
            >
              {exporting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowRight className="w-4 h-4" />
              )}
              {form.status === 'cyclist' ? 'Export as Cyclist' : 'Export to Rider'}
            </Button>
          )}
          {candidate?.imported_to_rider && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="px-3 py-2 bg-green-50 text-green-700 border-green-300">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Exported to Rider
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting}
                className="gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                title="Re-export if rider record is missing"
                data-testid="re-export-to-rider-btn"
              >
                {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                Re-export
              </Button>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TAP;
